import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faInfoCircle, faFileInvoice, faTruck, faShip, faHashtag, faHandshake, faFileContract, faArrowUp, faArrowDown, faArrowCircleUp, faArrowCircleDown, faBoxes, faProjectDiagram, faEye, faFileAlt, faTrash, faFilePdf, faFileWord, faEnvelope, faChevronRight, faSync, faUserShield, faGlobeAmericas, faCalculator, faTint, faMoon, faPlay, faAnchor, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import OperationsAccessList from './OperationsAccessList';
import TimeNavigationBar from './TimeNavigationBar';
import HamsterFloatingButton from './HamsterFloatingButton';
import TradeOpsDeleteModal from './TradeOpsDeleteModal';
import axiosInstance from '../utils/axiosConfig';
import './TradeOperationsLayout.css';
import Layout from './Layout';
import AssignDeliveryModal from './AssignDeliveryModal';
import ShipmentOverviewTab from './ShipmentOverview/ShipmentOverviewTab';
import RecapGroupingValidationModal from './RecapGroupingValidationModal';
import { buildRecapEntityVerdicts, recapEntityVerdict } from '../utils/recapEntityMatch';
import pdfHeaderLogo from '../assets/images/IMT_logo_pdf.png';
import jsPDF from 'jspdf';
import PdfPreviewModal from './shared/PdfPreviewModal';
import 'jspdf-autotable';
import hmsLogoBase64 from '../assets/logoBase64.json';
import { useUserInfo } from '../contexts/UserContext';
import { isRestrictedCustomerTrader } from '../utils/customerTraderAccess';
import { dashboardService } from '../services/dashboardService';
import { formatValidityDateTime, formatValidityDisplayDateTime, isValidityExpired } from '../utils/dateUtils';
import Footer from './ui/Footer';
import HMSPageHeader from './ui/HMSPageHeader';
import HMSSkeleton from './ui/HMSSkeleton';
import InvoiceRelationshipModal from './Invoicing/InvoiceRelationshipModal';
import { useFlashMessage } from './FlashMessage';
import { budgetService } from '../services/budgetService';
import { fuelEnquiryService } from '../services/fuelEnquiryService';
import FuelEnquiriesTab from './FuelEnquiries/FuelEnquiriesTab';
import BunkerPortsManager from './BunkerPortsManager';
import CargoPortsManager from './CargoPortsManager';
import { tradeOpsYearRange } from '../utils/tradeOpsDateRange';
import {
  isFreightRow,
  validateRecapGroupCompatibility,
} from '../utils/recapGroupCompatibility';
import { buildRecapDisplayName, RECAP_NAME_PROFILES } from '../utils/recapDisplayName';
import { severalLines } from '../utils/dealProductLines';
import { formatQuantityDisplay } from '../utils/quantityDisplay';

import usePinToDesk from './mydesk/usePinToDesk';
// Position codes barred from grouping recaps. Mirrors OPERATOR_POSITION_CODES
// in backend/operations_access/permissions.py -- keep the two in step.
const OPERATOR_POSITION_CODES = ['operator', 'senior_operator', 'operator_assistant'];

const generateGroupColor = (groupId) => {
  // Color pairs [background, text] - IMT green range with 15 distinct options
  // All colors are vibrant enough to be visible, avoiding overly light shades
  const colors = [
    ['#4a6b25', '#FFFFFF'],  // IMT Green (Primary)
    ['#5a7b35', '#FFFFFF'],  // IMT Light Green
    ['#3a5b15', '#FFFFFF'],  // IMT Dark Green
    ['#65a30d', '#FFFFFF'],  // Lime Green
    ['#4d7c0f', '#FFFFFF'],  // Olive Green
    ['#3f6212', '#FFFFFF'],  // Deep Olive
    ['#15803d', '#FFFFFF'],  // Forest Green
    ['#166534', '#FFFFFF'],  // Dark Forest
    ['#2f855a', '#FFFFFF'],  // Sea Green
    ['#38a169', '#FFFFFF'],  // Emerald
    ['#276749', '#FFFFFF'],  // Pine Green
    ['#6b8e23', '#FFFFFF'],  // Olive Drab
    ['#557a2e', '#FFFFFF'],  // Moss Green
    ['#4b6b1f', '#FFFFFF'],  // Fern Green
    ['#3d5a1f', '#FFFFFF'],  // Bottle Green
  ];
  
  // Simple hash function to get consistent color for same group
  const hash = groupId.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  
  const colorPair = colors[Math.abs(hash) % colors.length];
  return {
    backgroundColor: colorPair[0],
    color: colorPair[1]
  };
};

// Utility function to format datetime
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const time = date.toLocaleTimeString();
  return `${day}/${month}/${year} ${time}`;
};

const TradeOperationsLayout = ({ commodityType: commodityTypeProp = 'solids' } = {}) => {
  const history = useHistory();
  const location = useLocation();

  // Trade Ops flavour (Solids vs Fuels). Defaults to 'solids' for safety
  // when the layout is rendered without an explicit prop (e.g. legacy routes).
  const commodityType = (commodityTypeProp === 'fuels') ? 'fuels' : 'solids';
  const tradesBasePath = `/dashboard/trades/${commodityType}`;
  const flavourLabel = commodityType === 'fuels' ? 'Bunkers' : 'Cargo';

  // Restore state from navigation if available, otherwise use defaults
  const restoredDate = location.state?.selectedDate ? new Date(location.state.selectedDate) : new Date();
  const rawRestoredTab = location.state?.activeTab || 'shipment-overview';
  // Sprint 1D guard: a stale ``activeTab='fuel-enquiries'`` from a prior
  // Fuels session must NOT carry into a Solids navigation, otherwise the
  // tab-content block goes blank (the tab itself is gated by commodityType).
  // Additional guard: Bid/Offer ('deals') is hidden on Bunkers, so a stale
  // 'deals' tab from a Solids session must not carry into Fuels either.
  const restoredTab = (
    (rawRestoredTab === 'fuel-enquiries' && commodityType !== 'fuels') ||
    (rawRestoredTab === 'deals' && commodityType === 'fuels')
  )
    ? 'shipment-overview'
    : rawRestoredTab;
  
  const [selectedDate, setSelectedDate] = useState(restoredDate);
  const [deals, setDeals] = useState([]);
  const [tradeRecaps, setTradeRecaps] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [hoveredDealId, setHoveredDealId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const dropdownRefs = useRef({});
  const [groupingMode, setGroupingMode] = useState(false);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [groupingError, setGroupingError] = useState('');
  const [showPdfPopup, setShowPdfPopup] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfDocType, setPdfDocType] = useState('Deal');
  const [recapGroupingMode, setRecapGroupingMode] = useState(false);
  const [selectedRecaps, setSelectedRecaps] = useState([]);
  const [recapGroupingError, setRecapGroupingError] = useState('');
  const [showFormatPopup, setShowFormatPopup] = useState(false);
  const [dealToGenerate, setDealToGenerate] = useState(null);
  const [showBudgetFormatPopup, setShowBudgetFormatPopup] = useState(false);
  const [budgetToGenerate, setBudgetToGenerate] = useState(null);
  
  // Assign Delivery Modal state
  const [showAssignDeliveryModal, setShowAssignDeliveryModal] = useState(false);
  const [currentGroupRecaps, setCurrentGroupRecaps] = useState([]);
  
  // Validation Modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const { userInfo, updateUserInfo } = useUserInfo();
  const [activeTab, setActiveTab] = useState(restoredTab);

  // Customer traders have no delivery access (ClickUp Fuels flow whiteboard,
  // 2026-09-17): the server returns none, so the two delivery tabs would only
  // ever show an empty screen. Hidden, and a customer trader who lands on one
  // (the default tab, or a remembered one) is moved to their own work.
  const hideDeliveryTabs = isRestrictedCustomerTrader(userInfo);
  useEffect(() => {
    if (hideDeliveryTabs && (activeTab === 'shipment-overview' || activeTab === 'deliveries')) {
      setActiveTab(commodityType === 'fuels' ? 'fuel-enquiries' : 'recaps');
    }
  }, [hideDeliveryTabs, activeTab, commodityType]);
  
  // Check if user is Operations HOD (or admin)
  const isOperationsHOD = (userInfo.is_head_of_department && userInfo.supervised_department === 'Operations') || userInfo.is_superuser || userInfo.is_staff;

  // Budget Calculator tab visibility:
  // 1. Position contains "trade" (traders, senior traders, head of trade, trade finance, etc.)
  // 2. Operations HOD
  // 3. Superuser
  // 4. Operators who are members of a grouping with budgets
  const positionCode = (userInfo.position_code || '').toLowerCase();
  const canSeeBudgetTab = positionCode.includes('trade')
    || isOperationsHOD
    || userInfo.is_superuser
    || userInfo.has_budget_grouping_access;

  // Recap grouping: operators may not group/ungroup recaps. Traders and every
  // other role keep the access they already had, so this is a deny-list rather
  // than a traders-only allow-list -- admins, HODs and any other position must
  // be unaffected. Mirrors ``can_group_recaps`` on the backend, which is the
  // enforcing gate; an unknown position (user info still loading, or no
  // position set) leaves the control visible.
  const canGroupRecaps = isOperationsHOD || !OPERATOR_POSITION_CODES.includes(positionCode);

  // Ports tab visibility — one curated port list per flavour, each with its own
  // grant flag. Bunker Ports shows in the Fuels flavour, Cargo Ports in the
  // Solids flavour; a user with both flags sees the tab that matches the module
  // they are standing in, never the other module's list. Granting either
  // feature happens on the access screen (Role Assignment).
  const canManageBunkerPorts = userInfo.can_manage_bunker_ports || userInfo.is_superuser;
  const canManageCargoPorts = userInfo.can_manage_cargo_ports || userInfo.is_superuser;

  // Relationship Modal state
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [selectedDocumentForRelationship, setSelectedDocumentForRelationship] = useState(null);
  const [relationshipDocumentType, setRelationshipDocumentType] = useState(null);
  
  // Budget state
  const [budgets, setBudgets] = useState([]);
  const [budgetsLoading, setBudgetsLoading] = useState(false);

  // Sprint 1D: Fuel Enquiries count for the tab badge. Only fetched when
  // ``commodityType === 'fuels'`` so Solids users never hit the endpoint.
  // `null` means "not known", which is NOT the same as zero. Offline, an unanswerable
  // count used to render as "Bunker Enquiries (0)" next to a working
  // "Trade Recaps (6)" — indistinguishable from having no enquiries.
  const [fuelEnquiriesCount, setFuelEnquiriesCount] = useState(null);

  // Delete loading state
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Refresh loading state
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Flash message hook
  const { showSuccess, showError } = useFlashMessage();
  const { pin: pinToDesk } = usePinToDesk();

  // After useState declarations at the top, add this helper function
  
  // Get quarter string from date (e.g., "Q1 2023")
  const getQuarterString = (date) => {
    const month = date.getMonth();
    const quarter = Math.floor(month / 3) + 1; // Convert from 0-based to 1-based
    const year = date.getFullYear();
    return `Q${quarter} ${year}`;
  };

  // Fetch user info on mount to ensure UserContext is populated
  // This is critical for determining HOD access (Operations Access tab)
  // Without this, a direct page reload loses UserContext state since it only
  // gets populated by IntelligenceDashboard on normal navigation flow
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfoData = await dashboardService.getUserInfo();
        updateUserInfo(userInfoData);
      } catch (err) {
        console.error('Error fetching user info:', err);
      }
    };
    fetchUserInfo();
  }, [updateUserInfo]);

  // The grouping control renders before user info resolves (unknown position
  // fails open), so an operator can enter selection mode and then lose the
  // Cancel button when their position arrives. Drop back out of selection
  // mode rather than stranding them with a checkbox column they can't dismiss.
  useEffect(() => {
    if (!canGroupRecaps && recapGroupingMode) {
      setRecapGroupingMode(false);
      setSelectedRecaps([]);
      setRecapGroupingError('');
    }
  }, [canGroupRecaps, recapGroupingMode]);

  // Ensure we have user info when generating PDFs
  useEffect(() => {
    if (dealToGenerate && (!userInfo || !userInfo.office)) {
      const fetchUserInfo = async () => {
        try {
          const userInfoData = await dashboardService.getUserInfo();
          updateUserInfo(userInfoData);
        } catch (err) {
          console.error('Error fetching user info:', err);
        }
      };
      fetchUserInfo();
    }
  }, [dealToGenerate, userInfo, updateUserInfo]);

  // Fetch trade-ops data whenever the selected year or commodity changes.
  // Toggling `isRefreshing` here means the TimeNavigationBar refresh icon
  // spins during year navigation and stops once the fetch settles.
  useEffect(() => {
    let cancelled = false;
    setIsRefreshing(true);
    fetchData(selectedDate).finally(() => {
      if (!cancelled) setIsRefreshing(false);
    });
    return () => { cancelled = true; };
  }, [selectedDate, commodityType]);

  // Separate click-outside handler so it doesn't piggy-back on the fetch
  // effect (which previously caused refetches every time a dropdown opened).
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore clicks on gear buttons (they handle their own toggle)
      if (event.target.closest('.gear-button') || event.target.closest('.gear-icon')) {
        return;
      }
      
      // Check if click is on dropdown menu or action dropdown
      const clickedDropdown = event.target.closest('.action-dropdown');
      const clickedMenu = event.target.closest('.dropdown-menu');
      
      // If clicked outside both, close all dropdowns
      if (!clickedDropdown && !clickedMenu && dropdownVisible !== null) {
        setDropdownVisible(null);
        // Remove dropdown-open class from all action-dropdowns
        document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
          el.classList.remove('dropdown-open');
          const menu = el.querySelector('.dropdown-menu');
          if (menu) {
            menu.classList.remove('dropdown-upward');
          }
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownVisible]);

  // Separate useEffect for scrolling to top on mount only
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); // Empty dependency array means this runs once on mount

  // Sprint 1D: prime the Fuel Enquiries tab count badge. Only fires for the
  // Fuels flavour so Solids never makes a request to ``/api/fuel-enquiries/``.
  useEffect(() => {
    if (commodityType !== 'fuels') {
      setFuelEnquiriesCount(0);  // solids genuinely has none — a real zero
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fuelEnquiryService.list({
          page: 1,
          page_size: 1,
          commodityType: 'fuels',
        });
        if (!cancelled) {
          setFuelEnquiriesCount(typeof data.count === 'number' ? data.count : 0);
        }
      } catch (err) {
        if (!cancelled) {
          // Leave the count unknown rather than claiming zero.
          setFuelEnquiriesCount(null);
          console.error('Error fetching fuel enquiries count:', err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [commodityType]);

  // Fetch budgets when budget tab is active
  useEffect(() => {
    if (activeTab === 'budgets') {
      setBudgetsLoading(true);
      budgetService.listHeaders({ commodity_type: commodityType })
        .then(data => {
          setBudgets(Array.isArray(data) ? data : (data.results || []));
        })
        .catch(err => {
          console.error('Error fetching budgets:', err);
          setBudgets([]);
        })
        .finally(() => setBudgetsLoading(false));
    }
  }, [activeTab, commodityType]);

  const fetchData = async (date) => {
    try {
      // Full-year range based on the selected date's year, so the trade ops
      // screens show all deals/recaps/deliveries for that year.
      //
      // Computed by the shared helper, NOT inline: the offline pack has to
      // capture the exact same URL this screen asks for, and when this
      // calculation lived here the pack captured the unfiltered URL instead
      // and every tab rendered a confident zero offline (2026-08-11). One
      // function, two callers — see src/utils/tradeOpsDateRange.js.
      const { startDate, endDate } = tradeOpsYearRange(date);

      console.log('Fetching data for year range:', {
        year: date.getFullYear(),
        startDate,
        endDate
      });

      const commodityQuery = `&commodity_type=${commodityType}`;
      // Bunkers (fuels) has no Bid/Offer tab — skip the user-offers fetch
      // entirely so we don't waste a network call. Solids keeps the deals
      // fetch unchanged.
      const dealsPromise = commodityType === 'fuels'
        ? Promise.resolve({ data: [] })
        : axiosInstance.get(`/api/offer/user-offers/?start_date=${startDate}&end_date=${endDate}${commodityQuery}`);
      const [dealsResponse, recapsResponse, freightRecapsResponse, deliveriesResponse] = await Promise.all([
        dealsPromise,
        axiosInstance.get(`/api/offer/trade-recaps/?start_date=${startDate}&end_date=${endDate}${commodityQuery}`),
        axiosInstance.get(`/api/offer/freight-recaps/?start_date=${startDate}&end_date=${endDate}${commodityQuery}`),
        axiosInstance.get(`/api/deliveries/?start_date=${startDate}&end_date=${endDate}${commodityQuery}`)
      ]);

      console.log('Deals response:', dealsResponse.data);
      console.log('Trade recaps response:', recapsResponse.data);
      console.log('Freight recaps response:', freightRecapsResponse.data);
      console.log('Deliveries response:', deliveriesResponse.data);

      // Transform freight recaps to match trade recap structure
      const transformedFreightRecaps = freightRecapsResponse.data.map(freightRecap => ({
        id: freightRecap.id,
        transaction_number: freightRecap.transaction_number,
        recap_type: 'Freight',
        recap_display_name: freightRecap.recap_display_name,
        current_status: freightRecap.current_status,
        created_at: freightRecap.created_at,
        trade_recap_grouping_identifier: freightRecap.trade_recap_grouping_identifier || null,
        associated_offer_or_bid: null,
        purchase_or_sale: null,
        vessel_name: freightRecap.vessel_name || freightRecap.vessel || null,
        isFreightRecap: true,
        product: freightRecap.product || null,
        entity: freightRecap.entity || null,
        price_currency: freightRecap.currency || null,
        quantity_unit: freightRecap.quantity_unit || (freightRecap.quantity_mt != null ? 'MT' : null),
        load_port: freightRecap.load_port || null,
        discharge_port: freightRecap.discharge_port || null,
        quantity_mt: freightRecap.quantity_mt || null,
        currency: freightRecap.currency || null,
        laycan: freightRecap.laycan || null,
        laycan_comments: freightRecap.laycan_comments || null,
        // Counterparty for the freight auto-name (utils/recapDisplayName.js):
        // Owners is the other side of the deal, Charterer the fallback.
        owners: freightRecap.owners || null,
        charterer: freightRecap.charterer || null,
      }));

      // Combine trade recaps and transformed freight recaps
      const combinedRecaps = [...recapsResponse.data, ...transformedFreightRecaps];

      // Sort combined recaps by creation date (newest first)
      combinedRecaps.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      console.log('Combined recaps sorted by creation date (newest first):', 
        combinedRecaps.map(recap => ({
          id: recap.id,
          transaction_number: recap.transaction_number,
          created_at: recap.created_at,
          isFreightRecap: recap.isFreightRecap || false
        }))
      );

      // Log each recap's transaction number
      combinedRecaps.forEach(recap => {
        console.log('Recap details:', {
          id: recap.id,
          transaction_number: recap.transaction_number,
          recap_type: recap.recap_type,
          purchase_or_sale: recap.purchase_or_sale,
          isFreightRecap: recap.isFreightRecap || false
        });
      });

      console.log('FETCH: Setting state - Deals:', dealsResponse.data.length, 'Recaps:', combinedRecaps.length, 'Deliveries:', deliveriesResponse.data.length);
      setDeals(dealsResponse.data);
      setTradeRecaps(combinedRecaps);
      setDeliveries(deliveriesResponse.data);
      console.log('FETCH: State setters called');
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
    }
  };

  // Refresh handler
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes
    
    setIsRefreshing(true);
    try {
      await fetchData(selectedDate);
      console.log('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Navigation handlers
  const navState = () => ({
    selectedDate: selectedDate.toISOString(),
    activeTab: activeTab,
    commodityType: commodityType
  });
  const handleNewRecapSale = () => history.push('/trade-recap/new?type=Sale', navState());
  const handleNewRecapPurchase = () => history.push('/trade-recap/new?type=Purchase', navState());
  const handleNewRecapFreight = () => history.push('/freight-recap/new', navState());
  const handleNewDelivery = () => history.push('/deliveries/new', navState());

  // Grouping handlers
  const handleDealSelection = (dealId) => {
    setSelectedDeals(prev => 
      prev.includes(dealId) 
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const handleGroupDeals = async () => {
    if (selectedDeals.length < 2) {
      setGroupingError('Please select at least 2 deals to group');
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/offer/user-offers/update-groups/?commodity_type=${commodityType}`, {
        offer_ids: selectedDeals
      });

      if (response.data.status === 'success') {
        setGroupingMode(false);
        setSelectedDeals([]);
        setGroupingError('');
        fetchData(selectedDate); // Refresh the data
      }
    } catch (error) {
      console.error('Error grouping deals:', error);
      setGroupingError(error.response?.data?.error || 'Error grouping deals');
    }
  };

  const handleUngroupDeals = async () => {
    if (selectedDeals.length === 0) {
      setGroupingError('Please select at least one deal to ungroup');
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/offer/user-offers/ungroup/?commodity_type=${commodityType}`, {
        offer_ids: selectedDeals
      });

      if (response.data.status === 'success') {
        setGroupingMode(false);
        setSelectedDeals([]);
        setGroupingError('');
        fetchData(selectedDate); // Refresh the data
      }
    } catch (error) {
      setGroupingError(error.response?.data?.error || 'Error ungrouping deals');
    }
  };

  const handleGenerateClick = (deal) => {
    setDealToGenerate(deal);
    setShowFormatPopup(true);
  };

  // Manual Dormant toggle (PM request). 'Dormant' is the "trader parked this
  // deal" status - distinct from 'Fully Allocated' which the backend sets
  // automatically when recaps consume the full quantity. Sticky on the
  // server side (HMSUserOffer.recompute_status_from_recaps short-circuits
  // when status='Dormant') so the trader's intent isn't undone by a
  // subsequent recap save. Reactivation hands control back to the
  // automatic recompute by writing whichever status best fits the deal's
  // current recap state.
  const handleToggleDormant = async (deal) => {
    const dropdownEl = dropdownRefs.current[deal.offer_id];
    if (dropdownEl) {
      dropdownEl.classList.remove('dropdown-open');
      const menu = dropdownEl.querySelector('.dropdown-menu');
      if (menu) menu.classList.remove('dropdown-positioned-above');
    }
    setDropdownVisible(null);

    const isDormant = deal.offer_current_status === 'Dormant';
    // Reactivation derives the next status from current recap consumption
    // (the same logic the backend's recompute_status_from_recaps uses) so
    // the badge snaps directly to the right value with no flicker. The
    // consumed_quantity / is_fully_consumed fields are exposed on
    // HMSUserOfferSerializer. Backend recompute also runs on save as a
    // safety net for stale client state.
    const computeDerivedStatus = (d) => {
      const consumed = parseFloat(d.consumed_quantity);
      if (!Number.isFinite(consumed) || consumed <= 0) {
        return d.transaction_number ? 'Saved' : 'Draft';
      }
      return d.is_fully_consumed ? 'Fully Allocated' : 'Partial';
    };
    const nextStatus = isDormant ? computeDerivedStatus(deal) : 'Dormant';
    const previousStatus = deal.offer_current_status;

    // Optimistic update: flip the row in local state immediately so the badge
    // re-paints without waiting for the PATCH + full-year refetch round trip
    // (~1-2s on the current backend). Reverted in the catch block if the
    // PATCH fails so the UI never lies about persisted state.
    setDeals(prev => prev.map(d =>
      d.offer_id === deal.offer_id
        ? { ...d, offer_current_status: nextStatus }
        : d
    ));

    try {
      await axiosInstance.patch(`/api/offer/user-offers/${deal.offer_id}/`, {
        offer_current_status: nextStatus,
      });
      // Sync with the server in the background so the row reflects any
      // recompute that ran on the backend (e.g. reactivating a deal whose
      // recaps fully consume it will land it on 'Fully Allocated', not
      // 'Saved'). Not awaited critical for UX - optimistic update already
      // gave the user feedback.
      fetchData(selectedDate);
      showSuccess(
        isDormant
          ? `Reactivated ${deal.transaction_number || 'deal'}.`
          : `Marked ${deal.transaction_number || 'deal'} as Dormant.`
      );
    } catch (error) {
      console.error('Error toggling Dormant status:', error);
      // Revert optimistic update on failure so the badge matches reality.
      setDeals(prev => prev.map(d =>
        d.offer_id === deal.offer_id
          ? { ...d, offer_current_status: previousStatus }
          : d
      ));
      showError(
        error.response?.data?.error ||
          'Could not update the deal status. Please try again.'
      );
    }
  };

  const handleGenerateEmail = async (deal) => {
    try {
      // Create email subject from deal info
      const subject = `IMT Deal: ${deal.transaction_number} - ${deal.offer_transaction_type}`;
      
      // Create email body with similar formatting to Word doc
      let body = `IMT DEAL DETAILS\n`;
      body += `${'-'.repeat(50)}\n\n`;
      
      // Subject line: the deal's name (typed, else automatic; offer/deal_names.py)
      if (deal.display_name) {
        body += `SUBJECT: ${deal.display_name}\n${'='.repeat(deal.display_name.length + 9)}\n\n`;
      }
      
      // Add deal sections
      body += `1. SELLER\n${deal.selling_company_entity || 'N/A'}\n\n`;
      body += `2. BUYER\n${deal.buying_company_entity || 'N/A'}\n\n`;
      
      // Product & Origin
      const productMapping = {
        'Met Coke': 'Metallurgical Coke',
        'Pet Coke': 'Petroleum Coke'
      };
      const originMapping = {
        'Australia': 'Australian',
        'Columbia': 'Colombian',
        'Indonesia': 'Indonesian',
        'Mozambique': 'Mozambican',
        'Poland': 'Polish',
        'Russia': 'Russian',
        'Saudi Arabia': 'Saudi Arabian',
        'South Africa': 'South African',
        'USA': 'American',
        'Vietnam': 'Vietnamese'
      };
      
      const displayProduct = productMapping[deal.commodity_product_type] || deal.commodity_product_type || 'N/A';
      const displayOrigin = originMapping[deal.commodity_origin_country] || deal.commodity_origin_country || 'N/A';
      // Several products (backend/offer/product_lines.py): the offer text the
      // deal screen saved carries the product table; the header alone would
      // name only the first product.
      const severalProducts = severalLines(deal.products).length > 0;
      const commodityText = (severalProducts && deal.commodity_formatted_text
        ? deal.commodity_formatted_text.replace('Product & Origin: ', '')
        : null) || deal.commodity_custom_override || 
        `${displayProduct} in bulk of ${displayOrigin} Origin${deal.commodity_additional_details ? '\n' + deal.commodity_additional_details : ''}`;
      
      body += `3. PRODUCT & ORIGIN\n${commodityText}\n\n`;
      
      // Quantity - use dynamic unit and handle empty tolerance for liquids
      const unit = deal.quantity_unit || 'MT';
      
      // Build tolerance display only if values are present
      let toleranceText = '';
      if (deal.quantity_tolerance_lower_value || deal.quantity_tolerance_upper_value) {
        const lower = `${deal.quantity_tolerance_lower_value || ''}${deal.quantity_tolerance_lower_unit || '%'}`;
        const upper = `${deal.quantity_tolerance_upper_value || ''}${deal.quantity_tolerance_upper_unit || '%'}`;
        if (lower && upper) {
          toleranceText = ` with ${lower} to ${upper} tolerance`;
        } else if (lower) {
          toleranceText = ` with ${lower} tolerance`;
        }
      } else if (deal.quantity_tolerance_percentage) {
        toleranceText = ` with +/-${deal.quantity_tolerance_percentage}% tolerance`;
      }
      
      const quantityOptionText = deal.quantity_option_holder ? ` in ${deal.quantity_option_holder}` : '';
      const quantityText = (severalProducts && deal.quantity_formatted_text
        // Several products: the offer text the screen saved, with its product table.
        ? deal.quantity_formatted_text.replace('Quantity: ', '')
        : null) || deal.quantity_custom_override || 
        `${deal.quantity_in_metric_tons} ${unit}${toleranceText}${quantityOptionText}`;
      body += `4. QUANTITY\n${quantityText}\n\n`;
      
      body += `5. LOADING LAYCAN\n${deal.delivery_terms_and_conditions || 'N/A'}\n\n`;
      body += `6. INCOTERMS\n${deal.trade_incoterm_type ? `${deal.trade_incoterm_type} as per Incoterms® 2020` : 'N/A'}\n\n`;
      
      // Port details
      const portDetails = (() => {
        if (deal.loading_ports_details && deal.discharging_ports_details) {
          return {
            title: 'LOADING & DISCHARGING PORT(S)',
            content: `Loading: ${deal.loading_ports_details}\nDischarging: ${deal.discharging_ports_details}`
          };
        } else if (deal.loading_ports_details) {
          return {
            title: 'LOADING PORT(S)',
            content: deal.loading_ports_details
          };
        } else if (deal.discharging_ports_details) {
          return {
            title: 'DISCHARGING PORT(S)',
            content: deal.discharging_ports_details
          };
        }
        return {
          title: 'PORT(S)',
          content: 'N/A'
        };
      })();
      
      body += `7. ${portDetails.title}\n${portDetails.content}\n\n`;
      
      // Add qualities table as 8th point
      let qualitiesAdded = false;
      try {
        const qualityValuesResponse = await axiosInstance.get(`/api/offer/user-offers/${deal.offer_id}/quality-values/`);
        const qualityValues = qualityValuesResponse.data;

        if (qualityValues && qualityValues.length > 0) {
          qualitiesAdded = true;
          // Get quality parameters
          const qualityParamsResponse = await axiosInstance.get('/api/offer/quality-parameters/');
          const qualityParams = qualityParamsResponse.data;
          
          // Create parameter map
          const parameterMap = qualityParams.reduce((acc, param) => {
            acc[param.id] = param;
            return acc;
          }, {});

          // Get column headers - use dynamic headers from quality values
          const thirdColumnHeader = qualityValues[0]?.typical_or_guaranteed || 'Typical';
          const fourthColumnHeader = qualityValues[0]?.minmax_or_rejection || 'Min/Max';

          // Add qualities section
          body += `8. QUALITY SPECIFICATIONS\n`;
          body += `${'-'.repeat(100)}\n`;
          
          // Define column widths
          const PARAM_WIDTH = 35;
          const UNIT_WIDTH = 15;
          const GUARANTEED_WIDTH = 20;
          const REJECTION_WIDTH = 20;

          // Headers with exact spacing - use dynamic headers
          const parameterHeader = 'ASTM / ISO standard'.padEnd(PARAM_WIDTH);
          const unitHeader = 'Unit'.padEnd(UNIT_WIDTH);
          const typicalHeader = thirdColumnHeader.padEnd(GUARANTEED_WIDTH);
          const minMaxHeader = fourthColumnHeader.padEnd(REJECTION_WIDTH);
          body += `${parameterHeader}${unitHeader}${typicalHeader}${minMaxHeader}\n`;
          body += `${'-'.repeat(100)}\n`;

          // Add quality values with exact spacing
          qualityValues
            .filter(quality => 
              // Only include rows where at least one value is present
              quality.typical_or_guaranteed_value || quality.min_max_or_rejection_value
            )
            .forEach(quality => {
              const parameter = parameterMap[quality.quality_parameter_id];
              
              // Format each column with exact spacing
              let paramName, unit;
              
              if (quality.is_custom) {
                // For custom parameters, use property_name directly
                paramName = (quality.property_name || '').padEnd(PARAM_WIDTH);
                unit = (quality.unit || '').padEnd(UNIT_WIDTH);
              } else {
                // For standard parameters, use the parameter map as before
                paramName = (quality.selected_option || parameter?.description || '').padEnd(PARAM_WIDTH);
                unit = (parameter?.unit || '').padEnd(UNIT_WIDTH);
              }
              
              let guaranteedValue = (quality.typical_or_guaranteed_value || '').toString().padEnd(GUARANTEED_WIDTH);
              let rejectionValue = (quality.min_max_or_rejection_value || '').toString().padEnd(REJECTION_WIDTH);

              // Ensure values are properly spaced
              if (paramName.length > PARAM_WIDTH) {
                paramName = paramName.substring(0, PARAM_WIDTH);
              }
              
              // Add the row with exact spacing
              body += `${paramName}${unit}${guaranteedValue}${rejectionValue}\n`;
            });

          body += `${'-'.repeat(100)}\n\n`;
        }
      } catch (error) {
        console.error('Error adding qualities table to email:', error);
      }

      // Price - use dynamic unit
      const portType = deal.trade_incoterm_type === 'FOB' ? 'Loading Port(s)' : 'Discharging Port(s)';
      const priceText = (severalProducts && deal.price_formatted_text
        // Several products: the offer text the screen saved, with its product table.
        ? deal.price_formatted_text.replace('Base Price: ', '')
        : null) || deal.price_custom_override || 
        `${deal.price_determination_type !== 'Fix Price' ? `${deal.price_determination_type} plus ` : ''}${deal.price_currency_type} ${deal.price_fixed_amount || deal.price_premium_or_discount || '0,00'} per ${unit} ${deal.trade_incoterm_type} ${portType}${deal.price_kcal_per_kg ? ` ${deal.price_cv_basis_type} ${deal.price_kcal_per_kg} kcal/kg` : ''}${deal.price_determination_type !== 'Fix Price' && deal.price_index_applicable_period ? `\nApplicable Index Period: ${deal.price_index_applicable_period}` : ''}`;
      
      body += `${qualitiesAdded ? '9' : '8'}. BASE PRICE\n${priceText}\n\n`;
      
      // Add Quality Adjustments section - only show CV adjustments if kcal/kg is present (not applicable for liquids)
      const qualityAdjText = (() => {
        // If there's a custom override, use that
        if (deal.quality_adjustments_custom_override) {
          return deal.quality_adjustments_custom_override;
        }
        
        // Otherwise construct from components
        let text = '';
        
        // Only add pro-rata CV adjustments if kcal/kg is present (not for liquids)
        if (deal.price_kcal_per_kg) {
          if (deal.quality_prorata_cv_adjustments === 'Yes') {
            const cvBasis = deal.price_cv_basis_type || 'NCV(ARB)';
            const kcalKg = deal.price_kcal_per_kg;
            
            text += `If the actual ${cvBasis} as reported by the independent surveyor at the loading port is above or below ${kcalKg} kcal/kg, the Base Price shall be adjusted on pro rata basis in accordance with the following formula: Adjusted Price = Base price x Actual ${cvBasis} / ${kcalKg} kcal/kg`;
          } else if (deal.quality_prorata_cv_adjustments === 'Yes(Excl. Freight Portion)') {
            const cvBasis = deal.price_cv_basis_type || 'NCV(ARB)';
            const kcalKg = deal.price_kcal_per_kg;
            const currency = deal.price_currency_type || 'USD';
            
            text += `If the actual ${cvBasis} as reported by the independent surveyor at the loading port is above or below ${kcalKg} kcal/kg, the Price shall be adjusted on the FOB Portion on pro rata basis in accordance with the following formula:

FOB Base Price = ${currency} XX.XX (to be declared)
Freight Portion = ${currency} XX.XX (to be declared)

Adjusted ${deal.trade_incoterm_type} Price = FOB Base Price x Actual ${cvBasis} / ${kcalKg} kcal/kg + Freight Portion`;
          }
        }
        
        // Add other premium/penalties if present
        if (deal.quality_other_adjustments) {
          if (text) text += '\n\n';
          text += deal.quality_other_adjustments;
        }
        
        return text || 'None';
      })();
      
      body += `${qualitiesAdded ? '10' : '9'}. QUALITY ADJUSTMENTS\n${qualityAdjText}\n\n`;
      
      body += `${qualitiesAdded ? '11' : '10'}. PAYMENT\n${deal.payment_detailed_terms || 'N/A'}\n\n`;

      // Add Quality and Quantity Determination section
      const qualityQuantityText = (() => {
        // If there's a custom override, use that
        if (deal.quality_quantity_custom_override) {
          return deal.quality_quantity_custom_override;
        }
        
        // Otherwise construct the standard wording with surveyor details if available
        const surveyorDetails = deal.quality_quantity_surveyor_details || 'an internationally acknowledged Inspection Company';
        return `Quality and Quantity shall be determined at the Loading port by ${surveyorDetails} as appointed and paid by the Seller.\nQuantity will be determined by means of draft survey of the vessel. BL-Weight will be final and binding for both parties and basis for invoicing.`;
      })();
      
      body += `${qualitiesAdded ? '12' : '11'}. QUALITY AND QUANTITY DETERMINATION\n${qualityQuantityText}\n\n`;
      
      body += `${qualitiesAdded ? '13' : '12'}. SHIPPING TERMS\n${deal.shipping_detailed_conditions || 'N/A'}\n\n`;
      body += `${qualitiesAdded ? '14' : '13'}. OTHER TERMS\n${deal.other_terms_detailed_conditions || 'N/A'}\n\n`;
      
      // Validity
      const validityText = deal.offer_is_indication_only ? 
        'This offer is of indicative nature only and can be turned into a firm offer upon request and subject to Seller\'s reconfirmation and cargo unsold.' : 
        formatValidityDateTime(deal.deal_validity_date, deal.deal_validity_time, deal.deal_validity_timezone);
      
      body += `${qualitiesAdded ? '15' : '14'}. VALIDITY\n${validityText}\n\n`;

      // Encode the subject and body for mailto URL
      const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      // Open the default email client
      window.location.href = mailtoUrl;
      
    } catch (error) {
      console.error('Error generating email:', error);
    }
  };

  const handleFormatSelect = async (format) => {
    setShowFormatPopup(false);
    if (format === 'pdf') {
      handleGeneratePDF(dealToGenerate);
    } else if (format === 'docx') {
      handleGenerateWord(dealToGenerate);
    } else if (format === 'email') {
      handleGenerateEmail(dealToGenerate);
    }
  };

  const handleGenerateWord = async (deal) => {
    try {
      // Use the backend API to generate the Word document
      const response = await axiosInstance.post('/api/offer/generate-word/', deal, {
        responseType: 'blob'
      });
      
      // Create a blob from the response
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      
      // Create a download link and trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename with timestamp
      const now = new Date();
      const formattedDate = now.toISOString().replace(/[:.]/g, '-');
      const fileName = `IMT_Deal_${now.getTime()}_${formattedDate}.docx`;
      link.download = fileName;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating Word document:', error);
    }
  };

  const handleGeneratePDF = async (deal) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Define a safe margin for the footer area on first page
      const firstPageFooterMargin = 40; // Space to reserve for footer line (30px) + extra buffer

      // Add the IMT logo at the top right of the first page
      const headerImgWidth = 41; // 30 x 1400/1025, the logo's own aspect ratio
      const headerImgHeight = 30; // Adjust height as needed
      doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);

      // Add company information text under the logo but to the left
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('IMT International Minerals Trading AG', 14, 50);
      
      // Get the width of the bold text
      const boldTextWidth = doc.getStringUnitWidth('IMT International Minerals Trading AG') * 7 / doc.internal.scaleFactor;
      
      // Add the rest of the address after the bold text
      doc.setFont('helvetica', 'normal');
      doc.text(' | Firststrasse 15 | 8835 Feusisberg | Switzerland', 14 + boldTextWidth, 50);

      // Add user office and current date
      const currentDate = new Date();
      const formattedDate = currentDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      const officeAndDateText = `${userInfo.office || 'IMT Office'}, ${formattedDate}`;
      
      // Set font size specifically for office and date (same as address)
      doc.setFontSize(7);
      
      // Calculate right alignment position - align to right margin
      const officeAndDateWidth = doc.getStringUnitWidth(officeAndDateText) * 7 / doc.internal.scaleFactor;
      doc.text(officeAndDateText, pageWidth - 14 - officeAndDateWidth, 55);

      // Reset font size for content
      doc.setFontSize(10);

      // Reset text color for content (the old header styling was setting this to white)
      doc.setTextColor(0);

      // Start content after header
      let currentY = 65; // Position content below the header and company info text
      const lineHeight = 5;
      const indent = 5;

      // Subject line: the deal's name (typed, else automatic; offer/deal_names.py)
      if (deal.display_name) {
        // Check if we're on the first page and need to respect the footer margin
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        const safePageBreakY = currentPage === 1 ? pageHeight - firstPageFooterMargin : pageHeight - 20;
        
        if (currentY > safePageBreakY) {
          doc.addPage();
          // Add logo to the new page
          doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
          currentY = 50; // Start content lower on the page to avoid logo overlap
        }

        // Set bold font for subject line
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        
        // Add text
        const subjectText = `Subject: ${deal.display_name}`;
        doc.text(subjectText, 14, currentY);
        
        // Add underline
        const textWidth = doc.getStringUnitWidth(subjectText) * 11 / doc.internal.scaleFactor;
        doc.setDrawColor(0);
        doc.setLineWidth(0.5);
        doc.line(14, currentY + 1, 14 + textWidth, currentY + 1);
        
        // Increment position for next content
        currentY += lineHeight * 2;
      }

      // Function to add a numbered section with title and content
      const addNumberedSection = (number, title, content) => {
        // Check if we're on the first page and need to respect the footer margin
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        const safePageBreakY = currentPage === 1 ? pageHeight - firstPageFooterMargin : pageHeight - 20;
        
        if (currentY > safePageBreakY) {
          doc.addPage();
          // Add logo to the new page
          doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
          currentY = 50; // Start content lower on the page to avoid logo overlap
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(`${number}. ${title}:`, 14, currentY);
        currentY += lineHeight * 1.5;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        
        const maxWidth = pageWidth - 28;
        const splitContent = doc.splitTextToSize(content, maxWidth);
        
        for (let i = 0; i < splitContent.length; i++) {
          // Check page break condition with safe margin for first page
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          const safePageBreakY = currentPage === 1 ? pageHeight - firstPageFooterMargin : pageHeight - 20;
          
          if (currentY > safePageBreakY) {
            doc.addPage();
            // Add logo to the new page
            doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
            currentY = 50; // Start content lower on the page to avoid logo overlap
          }
          
          doc.text(splitContent[i], 14 + indent, currentY);
          currentY += lineHeight;
        }
        
        currentY += lineHeight * 1.5;
      };

      // Add sections - use formatted text if available
      const sellerText = deal.selling_company_formatted_text 
        ? deal.selling_company_formatted_text.replace('Seller: ', '')
        : deal.selling_company_entity || 'N/A';
      addNumberedSection('1', 'Seller', sellerText);
      
      const buyerText = deal.buying_company_formatted_text
        ? deal.buying_company_formatted_text.replace('Buyer: ', '')
        : deal.buying_company_entity || 'N/A';
      addNumberedSection('2', 'Buyer', buyerText);
      
      // Product & Origin - use formatted text if available
      let commodityText = deal.commodity_formatted_text;
      if (commodityText) {
        commodityText = commodityText.replace('Product & Origin: ', '');
      } else {
        const productMapping = {
          'Met Coke': 'Metallurgical Coke',
          'Pet Coke': 'Petroleum Coke'
        };
        const originMapping = {
          'Australia': 'Australian',
          'Columbia': 'Colombian',
          'Indonesia': 'Indonesian',
          'Mozambique': 'Mozambican',
          'Poland': 'Polish',
          'Russia': 'Russian',
          'Saudi Arabia': 'Saudi Arabian',
          'South Africa': 'South African',
          'USA': 'American',
          'Vietnam': 'Vietnamese'
        };
        const displayProduct = productMapping[deal.commodity_product_type] || deal.commodity_product_type || 'N/A';
        const displayOrigin = originMapping[deal.commodity_origin_country] || deal.commodity_origin_country || 'N/A';
        commodityText = deal.commodity_custom_override || 
          `${displayProduct} in bulk of ${displayOrigin} Origin${deal.commodity_additional_details ? '\n' + deal.commodity_additional_details : ''}`;
      }
      
      addNumberedSection('3', 'Product & Origin', commodityText);
      
      // Quantity - use formatted text if available
      let quantityText = deal.quantity_formatted_text;
      if (quantityText) {
        quantityText = quantityText.replace('Quantity: ', '');
      } else {
        const unit = deal.quantity_unit || 'MT';
        
        // Build tolerance display only if values are present
        let toleranceText = '';
        if (deal.quantity_tolerance_lower_value || deal.quantity_tolerance_upper_value) {
          const lower = `${deal.quantity_tolerance_lower_value || ''}${deal.quantity_tolerance_lower_unit || '%'}`;
          const upper = `${deal.quantity_tolerance_upper_value || ''}${deal.quantity_tolerance_upper_unit || '%'}`;
          if (lower && upper) {
            toleranceText = ` with ${lower} to ${upper} tolerance`;
          } else if (lower) {
            toleranceText = ` with ${lower} tolerance`;
          }
        } else if (deal.quantity_tolerance_percentage) {
          toleranceText = ` with +/-${deal.quantity_tolerance_percentage}% tolerance`;
        }
        
        const quantityOptionText = deal.quantity_option_holder ? ` in ${deal.quantity_option_holder}` : '';
        quantityText = deal.quantity_custom_override || 
          `${deal.quantity_in_metric_tons} ${unit}${toleranceText}${quantityOptionText}`;
      }
      addNumberedSection('4', 'Quantity', quantityText);
      
      // Delivery - use formatted text if available
      let deliveryText = deal.delivery_formatted_text;
      if (deliveryText) {
        deliveryText = deliveryText.replace('Delivery: ', '');
      } else {
        deliveryText = deal.delivery_terms_and_conditions || 'N/A';
      }
      addNumberedSection('5', 'Loading Laycan', deliveryText);
      
      // Incoterms - use formatted text if available
      let incotermText = deal.trade_incoterm_formatted_text;
      if (incotermText) {
        incotermText = incotermText.replace('Incoterms: ', '');
      } else {
        incotermText = deal.trade_incoterm_type ? `${deal.trade_incoterm_type} as per Incoterms® 2020` : 'N/A';
      }
      addNumberedSection('6', 'Incoterms', incotermText);

      // Loading/Discharging Ports - use formatted text if available
      let portText = deal.loading_ports_formatted_text;
      let portTitle = 'Port(s)';
      
      if (portText) {
        // Remove port type prefix if present
        const prefixes = ['Loading Port(s): ', 'Discharging Port(s): ', 'Loading & Discharging Port(s): '];
        for (const prefix of prefixes) {
          if (portText.startsWith(prefix)) {
            portText = portText.replace(prefix, '');
            break;
          }
        }
        
        // Determine title based on what's in the data
        if (deal.loading_ports_details && deal.discharging_ports_details) {
          portTitle = 'Loading & Discharging Port(s)';
        } else if (deal.trade_incoterm_type === 'FOB') {
          portTitle = 'Loading Port(s)';
        } else {
          portTitle = 'Discharging Port(s)';
        }
      } else {
        // Fallback to building from raw fields
        console.log('Checking port details:', {
          loading: deal.loading_ports_details,
          discharging: deal.discharging_ports_details
        });
        
        if (deal.loading_ports_details && deal.discharging_ports_details) {
          portTitle = 'Loading & Discharging Port(s)';
          portText = `Loading: ${deal.loading_ports_details}\nDischarging: ${deal.discharging_ports_details}`;
        } else if (deal.loading_ports_details) {
          portTitle = 'Loading Port(s)';
          portText = deal.loading_ports_details;
        } else if (deal.discharging_ports_details) {
          portTitle = 'Discharging Port(s)';
          portText = deal.discharging_ports_details;
        } else {
          portText = 'N/A';
        }
      }

      addNumberedSection('7', portTitle, portText);
      
      // Fetch and add qualities table before continuing with other sections
      let qualitiesTableAdded = false;
      try {
        const qualityValuesResponse = await axiosInstance.get(`/api/offer/user-offers/${deal.offer_id}/quality-values/`);
        const qualityValues = qualityValuesResponse.data;

        // Only add qualities table if there are values
        if (qualityValues && qualityValues.length > 0) {
          console.log('Adding qualities table to PDF with values:', qualityValues);
          qualitiesTableAdded = true;

          // Get quality parameters for each value
          const qualityParamsResponse = await axiosInstance.get('/api/offer/quality-parameters/');
          const qualityParams = qualityParamsResponse.data;
          
          // Create a map of parameter IDs to their details
          const parameterMap = qualityParams.reduce((acc, param) => {
            acc[param.id] = param;
            return acc;
          }, {});

          // Get the exact column headers as selected by the user
          const thirdColumnHeader = qualityValues[0]?.typical_or_guaranteed || 'Typical';
          const fourthColumnHeader = qualityValues[0]?.minmax_or_rejection || 'Min/Max';

          // Calculate table height and add new page if needed
          const tableHeight = (qualityValues.length + 1) * 20;
          const remainingSpace = pageHeight - currentY;

          if (tableHeight > remainingSpace) {
            doc.addPage();
            // Add logo to the new page
            doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
            currentY = 50; // Start content lower on the page to avoid logo overlap
          }

          // Add qualities section header
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text('8. Quality Specifications:', 14, currentY);
          currentY += lineHeight * 1.5;

          // Prepare table headers and data
          const qualitiesTableHeaders = [
            'ASTM / ISO standard', 
            'Unit', 
            thirdColumnHeader, 
            fourthColumnHeader
          ];

          const qualitiesTableData = qualityValues
            .filter(quality => 
              // Only include rows where at least one value is present
              quality.typical_or_guaranteed_value || quality.min_max_or_rejection_value
            )
            .map(quality => {
              const parameter = parameterMap[quality.quality_parameter_id];
              // Check if this is a custom quality parameter
              if (quality.is_custom) {
                // For custom parameters, use property_name and unit directly from the quality object
                return [
                  quality.property_name || '',
                  quality.unit || '',
                  quality.typical_or_guaranteed_value || '',
                  quality.min_max_or_rejection_value || ''
                ];
              } else {
                // For standard parameters, use the parameterMap as before
                return [
                  quality.selected_option || parameter?.description || '',
                  parameter?.unit || '',
                  quality.typical_or_guaranteed_value || '',
                  quality.min_max_or_rejection_value || ''
                ];
              }
            });

            // Add table to PDF
            doc.autoTable({
              head: [qualitiesTableHeaders],
              body: qualitiesTableData,
              startY: currentY,
              theme: 'grid',
              styles: {
                fontSize: 10,
                cellPadding: 2,
                lineColor: [0, 0, 0],
                lineWidth: 0.1,
                overflow: 'linebreak',
                cellWidth: 'wrap'
              },
              headStyles: {
                fillColor: [255, 255, 255],
                textColor: 0,
                fontStyle: 'bold'
              },
              alternateRowStyles: {
                fillColor: [255, 255, 255]
              },
              columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 30 },
                2: { cellWidth: 'auto' },
                3: { cellWidth: 'auto' }
              },
              margin: { 
                left: 14, 
                right: 14,
                top: 45, // Ensure tables in new pages leave space for the logo
                bottom: doc.internal.getCurrentPageInfo().pageNumber === 1 ? firstPageFooterMargin : 20 
              },
              didDrawPage: function(data) {
                // Add logo to each new page created by autoTable
                doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
              },
              didParseCell: function(data) {
                if (data.section === 'head') {
                  data.cell.styles.halign = 'center';
                } else {
                  if (data.column.index === 0) {
                    data.cell.styles.halign = 'left';
                  } else {
                    data.cell.styles.halign = 'center';
                  }
                }
              }
            });

            currentY = doc.autoTable.previous.finalY + lineHeight * 2;
        }
      } catch (error) {
        console.error('Error adding qualities table:', error);
      }
      
      // Base Price - use formatted text if available
      let priceText = deal.price_formatted_text;
      if (priceText) {
        priceText = priceText.replace('Base Price: ', '');
      } else {
        const unit = deal.quantity_unit || 'MT';
        const portType = deal.trade_incoterm_type === 'FOB' ? 'Loading Port(s)' : 'Discharging Port(s)';
        priceText = deal.price_custom_override || 
          `${deal.price_determination_type !== 'Fix Price' ? `${deal.price_determination_type} plus ` : ''}${deal.price_currency_type} ${deal.price_fixed_amount || deal.price_premium_or_discount || '0,00'} per ${unit} ${deal.trade_incoterm_type} ${portType}${deal.price_kcal_per_kg ? ` ${deal.price_cv_basis_type} ${deal.price_kcal_per_kg} kcal/kg` : ''}${deal.price_determination_type !== 'Fix Price' && deal.price_index_applicable_period ? `\nApplicable Index Period: ${deal.price_index_applicable_period}` : ''}`;
      }
      
      // Deal Validity - use formatted text if available
      let validityText = deal.deal_validity_formatted_text;
      if (!validityText) {
        validityText = deal.offer_is_indication_only ? 
          'This offer is of indicative nature only and can be turned into a firm offer upon request and subject to Seller\'s reconfirmation and cargo unsold.' : 
          formatValidityDateTime(deal.deal_validity_date, deal.deal_validity_time, deal.deal_validity_timezone);
      }

      // Add remaining sections with adjusted numbering
      addNumberedSection(qualitiesTableAdded ? '9' : '8', 'Base Price', priceText);
      
      // Add Quality Adjustments section - only show CV adjustments if kcal/kg is present (not applicable for liquids)
      const qualityAdjText = (() => {
        // If there's a custom override, use that
        if (deal.quality_adjustments_custom_override) {
          return deal.quality_adjustments_custom_override;
        }
        
        // Otherwise construct from components
        let text = '';
        
        // Only add pro-rata CV adjustments if kcal/kg is present (not for liquids)
        if (deal.price_kcal_per_kg) {
          if (deal.quality_prorata_cv_adjustments === 'Yes') {
            const cvBasis = deal.price_cv_basis_type || 'NCV(ARB)';
            const kcalKg = deal.price_kcal_per_kg;
            
            text += `If the actual ${cvBasis} as reported by the independent surveyor at the loading port is above or below ${kcalKg} kcal/kg, the Base Price shall be adjusted on pro rata basis in accordance with the following formula: Adjusted Price = Base price x Actual ${cvBasis} / ${kcalKg} kcal/kg`;
          } else if (deal.quality_prorata_cv_adjustments === 'Yes(Excl. Freight Portion)') {
            const cvBasis = deal.price_cv_basis_type || 'NCV(ARB)';
            const kcalKg = deal.price_kcal_per_kg;
            const currency = deal.price_currency_type || 'USD';
            
            text += `If the actual ${cvBasis} as reported by the independent surveyor at the loading port is above or below ${kcalKg} kcal/kg, the Price shall be adjusted on the FOB Portion on pro rata basis in accordance with the following formula:

FOB Base Price = ${currency} XX.XX (to be declared)
Freight Portion = ${currency} XX.XX (to be declared)

Adjusted ${deal.trade_incoterm_type} Price = FOB Base Price x Actual ${cvBasis} / ${kcalKg} kcal/kg + Freight Portion`;
          }
        }
        
        // Add other premium/penalties if present
        if (deal.quality_other_adjustments) {
          if (text) text += '\n\n';
          text += deal.quality_other_adjustments;
        }
        
        return text || 'None';
      })();
      
      addNumberedSection(qualitiesTableAdded ? '10' : '9', 'Quality Adjustments', qualityAdjText);
      
      addNumberedSection(qualitiesTableAdded ? '11' : '10', 'Payment', deal.payment_detailed_terms || 'N/A');
      
      // Quality and Quantity Determination - use formatted text if available
      let qualityQuantityText = deal.quality_quantity_formatted_text;
      if (qualityQuantityText) {
        qualityQuantityText = qualityQuantityText.replace('Quality and Quantity Determination: ', '');
      } else {
        qualityQuantityText = (() => {
          // If there's a custom override, use that
          if (deal.quality_quantity_custom_override) {
            return deal.quality_quantity_custom_override;
          }
          
          // Otherwise construct the standard wording with surveyor details if available
          const surveyorDetails = deal.quality_quantity_surveyor_details || 'an internationally acknowledged Inspection Company';
          return `Quality and Quantity shall be determined at the Loading port by ${surveyorDetails} as appointed and paid by the Seller.\nQuantity will be determined by means of draft survey of the vessel. BL-Weight will be final and binding for both parties and basis for invoicing.`;
        })();
      }
      
      addNumberedSection(qualitiesTableAdded ? '12' : '11', 'Quality and Quantity Determination', qualityQuantityText);
      
      addNumberedSection(qualitiesTableAdded ? '13' : '12', 'Shipping Terms', deal.shipping_detailed_conditions || 'N/A');
      addNumberedSection(qualitiesTableAdded ? '14' : '13', 'Other Terms', deal.other_terms_detailed_conditions || 'N/A');
      addNumberedSection(qualitiesTableAdded ? '15' : '14', 'Validity', validityText);

      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        // Add the logo to every page (in case some pages were created without it)
        doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
        
        // Only add page numbers to pages other than the first
        if (i > 1) {
          // Format: current/total in center of page with HMS blue color
          doc.setFontSize(8);
          doc.setTextColor(2, 119, 190); // HMS Blue color
          
          // Create the page number text in format: current/total
          const pageText = `${i} / ${pageCount}`;
          
          // Calculate the center position of the page
          const textWidth = doc.getStringUnitWidth(pageText) * 8 / doc.internal.scaleFactor;
          const centerX = (pageWidth - textWidth) / 2;
          
          // Position at bottom of page
          doc.text(pageText, centerX, pageHeight - 10);
        }
        
        // Add blue divider line to first page only
        if (i === 1) {
          doc.setDrawColor(2, 119, 190); // HMS Blue color
          doc.setLineWidth(0.3);
          
          // Calculate position (30px from left/right edges, 30px from bottom)
          const lineY = pageHeight - 30;
          const lineStartX = 15;
          const lineEndX = pageWidth - 15;
          
          // Draw the line
          doc.line(lineStartX, lineY, lineEndX, lineY);
          
          // Add footer text blocks below the line
          const footerFontSize = 6;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(footerFontSize);
          doc.setTextColor(2, 119, 190); // HMS Blue color for footer text
          
          // Calculate total available width
          const totalWidth = lineEndX - lineStartX;
          const blockWidth = totalWidth / 4; // Four equal width blocks
          
          // Define text blocks with bold indicators
          const footerBlocks = [
            [
              // IMT's imprint, the same data as the invoice footer (backend invoicing/constants.py).
              // The legal name takes two lines so it stays clear of the next block.
              { text: 'IMT International Minerals', bold: true },
              { text: 'Trading AG', bold: true },
              { text: 'Firststrasse 15', bold: false },
              { text: '8835 Feusisberg', bold: false },
              { text: 'Switzerland', bold: false }
            ],
            [
              { text: 'P', bold: true, suffix: ' +41 55 220 2000' },
              { text: 'M', bold: true, suffix: ' info@imt-ag.ch' },
              { text: 'W', bold: true, suffix: ' www.imt-ag.ch' }
            ],
            [
              { text: 'Chairman of the supervisory board', bold: true },
              { text: 'Urban Baumann', bold: false }
            ],
            [
              { text: 'Registration number', bold: true, suffix: ' CHE-131.511.260' },
              { text: 'Handelsregister Schwyz', bold: true },
              { text: 'VAT ID', bold: true, suffix: ' CHE-131.511.260 MWST' }
            ]
          ];
          
          // Render each block
          footerBlocks.forEach((block, index) => {
            const blockX = lineStartX + (blockWidth * index);
            const textY = lineY + 7; // Start text 7 units below the line
            const lineHeight = 3.5; // Height between lines
            
            // Apply a left shift to blocks 2, 3, and 4
            const leftShift = index > 0 ? 10 : 0; // 10 pixels to the left for blocks 2, 3, 4
            const adjustedBlockX = blockX - leftShift;
            
            block.forEach((item, lineIndex) => {
              const y = textY + (lineIndex * lineHeight);
              
              // Set bold or normal font as needed
              doc.setFont('helvetica', item.bold ? 'bold' : 'normal');
              
              // Draw the bold part
              doc.text(item.text, adjustedBlockX, y);
              
              // Draw the normal suffix if it exists
              if (item.suffix) {
                // Calculate width of bold text to position suffix
                const boldWidth = doc.getStringUnitWidth(item.text) * footerFontSize / doc.internal.scaleFactor;
                doc.setFont('helvetica', 'normal');
                doc.text(item.suffix, adjustedBlockX + boldWidth, y);
              }
            });
          });
          
          // Add logging for debugging
          console.log('Added footer text blocks below divider line');
        }
      }

      // Save the PDF to a blob
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
      setPdfDocType('Deal');
      setShowPdfPopup(true);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      const now = new Date();
      const formattedDate = now.toISOString().replace(/[:.]/g, '-');
      const fileName = `IMT_${pdfDocType}_${now.getTime()}_${formattedDate}.pdf`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setShowPdfPopup(false);
  };

  // Action handlers - with dropdown positioning logic
  const toggleDropdown = (id) => {
    const newActiveDropdown = dropdownVisible === id ? null : id;
    setDropdownVisible(newActiveDropdown);
    
    // FIRST: Always remove dropdown-open from ALL dropdowns
    document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
      el.classList.remove('dropdown-open');
      const menu = el.querySelector('.dropdown-menu');
      if (menu) {
        menu.classList.remove('dropdown-upward');
      }
    });
    
    // If opening a dropdown, calculate position after render
    if (newActiveDropdown) {
      setTimeout(() => {
        // Find the dropdown element - check all possible ID formats
        let dropdownElement = dropdownRefs.current[id];
        
        if (dropdownElement) {
          const dropdownMenu = dropdownElement.querySelector('.dropdown-menu');
          const actionDropdown = dropdownElement;
          
          if (dropdownMenu && actionDropdown) {
            // Add dropdown-open class to parent for z-index elevation
            actionDropdown.classList.add('dropdown-open');
            
            // Calculate if dropdown would go off-screen
            const rect = dropdownMenu.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - rect.bottom;
            
            // If less than 50px space below or dropdown is cut off, display upward
            if (spaceBelow < 50 || rect.bottom > viewportHeight) {
              dropdownMenu.classList.add('dropdown-upward');
            } else {
              dropdownMenu.classList.remove('dropdown-upward');
            }
          }
        }
      }, 10);
    }
  };

  const closeDropdown = () => {
    setDropdownVisible(null);
    // Clean up dropdown classes
    document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
      el.classList.remove('dropdown-open');
      const menu = el.querySelector('.dropdown-menu');
      if (menu) {
        menu.classList.remove('dropdown-upward');
      }
    });
  };

  const viewDeal = (dealId, dealType, transactionType, transactionNumber) => {
    // If dealType is undefined, derive it from transactionType
    const templateType = dealType || transactionType?.toLowerCase() || 'offer';
    // Bid/Offer is Cargo-only now; this is only reachable from the Solids
    // Bid/Offer tab. Route straight to /offer-generator.
    history.push(`/offer-generator?offerId=${dealId}&type=${templateType}&dealType=${transactionType}&transactionNumber=${transactionNumber}`, {
      selectedDate: selectedDate.toISOString(),
      activeTab: activeTab,
      commodityType: commodityType
    });
  };

  const viewRecap = (recapId) => {
    // Find the recap to determine its type
    const recap = tradeRecaps.find(r => r.id === recapId);
    if (recap && recap.transaction_number?.startsWith('F')) {
      history.push(`/freight-recap/${recapId}`, {
        selectedDate: selectedDate.toISOString(),
        activeTab: activeTab,
        commodityType: commodityType
      });
    } else {
      history.push(`/trade-recap/${recapId}`, {
        selectedDate: selectedDate.toISOString(),
        activeTab: activeTab,
        commodityType: commodityType
      });
    }
  };

  const deleteDeal = async (dealId) => {
    setIsDeleting(true);
    try {
      console.log('DELETE: Starting deletion of deal:', dealId);
      console.log('DELETE: Current deals count before deletion:', deals.length);
      
      await axiosInstance.delete(`/api/offer/user-offers/${dealId}/`);
      console.log('DELETE: Backend deletion successful');
      
      await fetchData(selectedDate);
      console.log('DELETE: fetchData completed');
      
      // Small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 100));
      
      showSuccess('Deal deleted successfully');
      setShowPopup(false);
    } catch (error) {
      console.error('Error deleting deal:', error);
      showError('Failed to delete deal. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteRecap = async (recapId) => {
    setIsDeleting(true);
    try {
      console.log('DELETE: Starting deletion of recap:', recapId);
      console.log('DELETE: Current recaps count before deletion:', tradeRecaps.length);
      
      // Find the recap to determine its type
      const recap = tradeRecaps.find(r => r.id === recapId);
      
      if (recap && recap.isFreightRecap) {
        // Handle freight recap deletion
        console.log('Deleting freight recap:', recapId);
        await axiosInstance.delete(`/api/offer/freight-recaps/${recapId}/`);
      } else {
        // Handle trade recap deletion (existing logic)
        console.log('Deleting trade recap:', recapId);
        
        // First get the recap to find its associated offer
        const recapResponse = await axiosInstance.get(`/api/offer/trade-recaps/${recapId}/`);
        const offerId = recapResponse.data.offer;

        // Delete the recap
        await axiosInstance.delete(`/api/offer/trade-recaps/${recapId}/`);

        // Update the associated deal's status back to Draft
        if (offerId) {
          await axiosInstance.patch(`/api/offer/user-offers/${offerId}/`, {
            offer_current_status: 'Draft'
          });
        }
      }

      console.log('DELETE: Backend deletion successful');
      await fetchData(selectedDate);
      console.log('DELETE: fetchData completed');
      
      // Small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 100));
      
      showSuccess('Recap deleted successfully');
      setShowPopup(false);
    } catch (error) {
      console.error('Error deleting recap:', error);
      
      // Display backend error message if available
      const errorMessage = error.response?.data?.error || 'Failed to delete recap. Please try again.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Add deleteDelivery function
  const deleteDelivery = async (deliveryId) => {
    setIsDeleting(true);
    try {
      console.log('DELETE: Starting deletion of delivery:', deliveryId);
      console.log('DELETE: Current deliveries count before deletion:', deliveries.length);
      
      await axiosInstance.delete(`/api/deliveries/${deliveryId}/`);
      console.log('DELETE: Backend deletion successful');
      
      // Refresh data
      await fetchData(selectedDate);
      console.log('DELETE: fetchData completed');
      
      // Small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 100));
      
      showSuccess('Delivery deleted successfully');
      setShowPopup(false);
    } catch (error) {
      console.error('Error deleting delivery:', error);
      showError('Failed to delete delivery. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteBudget = async (budgetId) => {
    setIsDeleting(true);
    try {
      await budgetService.deleteHeader(budgetId);

      const data = await budgetService.listHeaders();
      setBudgets(Array.isArray(data) ? data : (data.results || []));

      await new Promise(resolve => setTimeout(resolve, 100));

      showSuccess('Budget deleted successfully');
      setShowPopup(false);
    } catch (error) {
      console.error('Error deleting budget:', error);
      showError('Failed to delete budget. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ---- Budget export handlers ------------------------------------------------

  const BUDGET_SECTION_LABELS = {
    PRODUCT_SALES: 'Outgoing Invoices / Credit Notes',
    PRODUCT_PURCHASE: 'Incoming Invoices / Debit Notes',
    FREIGHT: 'Freight',
    COMMISSIONS: 'Commissions',
    PURCHASING_COSTS: 'Purchasing Costs',
    LAYTIME: 'Laytime Calculations',
    FINANCING: 'Financing',
    OTHER_EXPENSES: 'Other Expected Expenses',
    BELOW_GROSS_ADJUSTMENTS: 'Below-Gross Adjustments',
  };

  const BUDGET_SECTION_ORDER = [
    'PRODUCT_SALES',
    'PRODUCT_PURCHASE',
    'FREIGHT',
    'COMMISSIONS',
    'PURCHASING_COSTS',
    'LAYTIME',
    'FINANCING',
    'OTHER_EXPENSES',
    'BELOW_GROSS_ADJUSTMENTS',
  ];

  const handleBudgetFormatSelect = async (format) => {
    setShowBudgetFormatPopup(false);
    if (format === 'pdf') {
      handleGenerateBudgetPDF(budgetToGenerate);
    } else if (format === 'docx') {
      handleGenerateBudgetWord(budgetToGenerate);
    } else if (format === 'email') {
      handleGenerateBudgetEmail(budgetToGenerate);
    }
  };

  const handleGenerateBudgetPDF = async (budget) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const lines = budget.lines || [];

      const firstPageFooterMargin = 40;
      const headerImgWidth = 41; // 30 x 1400/1025, the logo's own aspect ratio
      const headerImgHeight = 30;

      doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('IMT International Minerals Trading AG', 14, 50);
      const boldTextWidth = doc.getStringUnitWidth('IMT International Minerals Trading AG') * 7 / doc.internal.scaleFactor;
      doc.setFont('helvetica', 'normal');
      doc.text(' | Firststrasse 15 | 8835 Feusisberg | Switzerland', 14 + boldTextWidth, 50);

      doc.setFontSize(10);
      doc.setTextColor(0);

      let currentY = 60;
      const hmsBlue = [2, 119, 190];

      // Subject
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      const subjectText = `Budget: ${budget.display_name || 'Untitled'}`;
      doc.text(subjectText, 14, currentY);
      const subjectWidth = doc.getStringUnitWidth(subjectText) * 11 / doc.internal.scaleFactor;
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(14, currentY + 1, 14 + subjectWidth, currentY + 1);
      currentY += 10;

      // Summary info
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const qty = budget.quantity ? formatQuantityDisplay(budget.quantity) : '0';
      doc.text(`Quantity: ${qty}    |    Currency: ${budget.currency_code}    |    Status: ${budget.status || 'DRAFT'}`, 14, currentY);
      currentY += 8;

      // Render each section as a table
      BUDGET_SECTION_ORDER.forEach((sectionCode) => {
        const sectionLines = lines.filter(l => l.section === sectionCode && !l.is_margin_row);
        const dataRows = sectionLines.filter(l => !l.is_subtotal);
        if (dataRows.length === 0) return;

        const subtotalLine = sectionLines.find(l => l.is_subtotal);

        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        const safePageBreakY = currentPage === 1 ? pageHeight - firstPageFooterMargin : pageHeight - 20;
        if (currentY > safePageBreakY) {
          doc.addPage();
          doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
          currentY = 50;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(hmsBlue[0], hmsBlue[1], hmsBlue[2]);
        doc.text(BUDGET_SECTION_LABELS[sectionCode] || sectionCode, 14, currentY);
        doc.setTextColor(0);
        currentY += 4;

        const tableBody = dataRows.map(l => [
          l.line_type || '-',
          l.partner_name || '-',
          l.description || l.line_label || '-',
          l.amount_per_unit != null ? parseFloat(l.amount_per_unit).toLocaleString('en-US', { minimumFractionDigits: 4 }) : '-',
          l.percent_of_revenue != null ? `${parseFloat(l.percent_of_revenue).toFixed(2)}%` : '-',
          l.budget_amount != null ? parseFloat(l.budget_amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-',
        ]);

        if (subtotalLine) {
          tableBody.push([
            { content: 'Subtotal', colSpan: 3, styles: { fontStyle: 'bold', halign: 'right' } },
            '',
            '',
            { content: subtotalLine.budget_amount != null ? parseFloat(subtotalLine.budget_amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-', styles: { fontStyle: 'bold' } },
          ]);
        }

        doc.autoTable({
          startY: currentY,
          head: [['Type', 'Partner', 'Description', 'Amt/Unit', '%', 'Budget Amount']],
          body: tableBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: hmsBlue, textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 28 },
            3: { halign: 'right' },
            4: { halign: 'right', cellWidth: 14 },
            5: { halign: 'right' },
          },
          margin: {
            left: 14,
            right: 14,
            top: 45,
            bottom: doc.internal.getCurrentPageInfo().pageNumber === 1 ? firstPageFooterMargin : 20
          },
          didDrawPage: (data) => {
            doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
          },
        });

        currentY = doc.lastAutoTable.finalY + 8;
      });

      // Margin summary
      const grossMarginLine = lines.find(l => l.is_margin_row && l.line_label && l.line_label.toLowerCase().includes('gross'));
      const netMarginLine = lines.find(l => l.is_margin_row && l.line_label && l.line_label.toLowerCase().includes('net'));

      if (grossMarginLine || netMarginLine) {
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        const safePageBreakY = currentPage === 1 ? pageHeight - firstPageFooterMargin : pageHeight - 20;
        if (currentY > safePageBreakY) {
          doc.addPage();
          doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);
          currentY = 50;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(hmsBlue[0], hmsBlue[1], hmsBlue[2]);
        doc.text('Margin Summary', 14, currentY);
        doc.setTextColor(0);
        currentY += 4;

        const marginBody = [];
        if (grossMarginLine) {
          const val = parseFloat(grossMarginLine.budget_amount) || 0;
          marginBody.push(['Gross Margin', `${budget.currency_code} ${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, val > 0 ? 'Profit' : val < 0 ? 'Loss' : '-']);
        }
        if (netMarginLine) {
          const val = parseFloat(netMarginLine.budget_amount) || 0;
          marginBody.push(['Net Margin', `${budget.currency_code} ${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, val > 0 ? 'Profit' : val < 0 ? 'Loss' : '-']);
        }

        doc.autoTable({
          startY: currentY,
          head: [['Margin', 'Amount', 'Status']],
          body: marginBody,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: hmsBlue, textColor: 255, fontStyle: 'bold' },
          columnStyles: { 1: { halign: 'right' } },
          margin: { left: 14, right: 14 },
        });
      }

      // Final pass: logo on every page, page numbers on pages 2+, footer on page 1
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        doc.addImage(pdfHeaderLogo, 'PNG', pageWidth - headerImgWidth - 10, 10, headerImgWidth, headerImgHeight);

        if (i > 1) {
          doc.setFontSize(8);
          doc.setTextColor(2, 119, 190);
          const pageText = `${i} / ${pageCount}`;
          const textWidth = doc.getStringUnitWidth(pageText) * 8 / doc.internal.scaleFactor;
          const centerX = (pageWidth - textWidth) / 2;
          doc.text(pageText, centerX, pageHeight - 10);
        }

        if (i === 1) {
          doc.setDrawColor(2, 119, 190);
          doc.setLineWidth(0.3);
          const lineY = pageHeight - 30;
          const lineStartX = 15;
          const lineEndX = pageWidth - 15;
          doc.line(lineStartX, lineY, lineEndX, lineY);

          const footerFontSize = 6;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(footerFontSize);
          doc.setTextColor(2, 119, 190);

          const totalWidth = lineEndX - lineStartX;
          const blockWidth = totalWidth / 4;

          // The same IMT block the invoice footer prints (HMS_BERGBAU_AG_INFO in
          // backend/invoicing/constants.py); its blank fax, CEO and CFO are left out.
          const footerBlocks = [
            [
              // IMT's imprint, the same data as the invoice footer (backend invoicing/constants.py).
              // The legal name takes two lines so it stays clear of the next block.
              { text: 'IMT International Minerals', bold: true },
              { text: 'Trading AG', bold: true },
              { text: 'Firststrasse 15', bold: false },
              { text: '8835 Feusisberg', bold: false },
              { text: 'Switzerland', bold: false }
            ],
            [
              { text: 'P', bold: true, suffix: ' +41 55 220 2000' },
              { text: 'M', bold: true, suffix: ' info@imt-ag.ch' },
              { text: 'W', bold: true, suffix: ' www.imt-ag.ch' }
            ],
            [
              { text: 'Chairman of the supervisory board', bold: true },
              { text: 'Urban Baumann', bold: false }
            ],
            [
              { text: 'Registration number', bold: true, suffix: ' CHE-131.511.260' },
              { text: 'Handelsregister Schwyz', bold: true },
              { text: 'VAT ID', bold: true, suffix: ' CHE-131.511.260 MWST' }
            ]
          ];

          footerBlocks.forEach((block, index) => {
            const blockX = lineStartX + (blockWidth * index);
            const textY = lineY + 7;
            const footerLineHeight = 3.5;

            const leftShift = index > 0 ? 10 : 0;
            const adjustedBlockX = blockX - leftShift;

            block.forEach((item, lineIndex) => {
              const y = textY + (lineIndex * footerLineHeight);
              doc.setFont('helvetica', item.bold ? 'bold' : 'normal');
              doc.text(item.text, adjustedBlockX, y);

              if (item.suffix) {
                const boldWidth = doc.getStringUnitWidth(item.text) * footerFontSize / doc.internal.scaleFactor;
                doc.setFont('helvetica', 'normal');
                doc.text(item.suffix, adjustedBlockX + boldWidth, y);
              }
            });
          });
        }
      }

      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setPdfDocType('Budget');
      setShowPdfPopup(true);
    } catch (error) {
      console.error('Error generating budget PDF:', error);
      showError('Failed to generate budget PDF.');
    }
  };

  const handleGenerateBudgetWord = async (budget) => {
    try {
      const response = await axiosInstance.post('/api/budget/generate-word/', { budget_id: budget.id }, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const formattedDate = now.toISOString().replace(/[:.]/g, '-');
      link.download = `IMT_Budget_${now.getTime()}_${formattedDate}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating budget Word document:', error);
      showError('Failed to generate budget Word document.');
    }
  };

  const handleGenerateBudgetEmail = (budget) => {
    try {
      const budgetLines = budget.lines || [];
      const subject = `Budget: ${budget.display_name || 'Untitled'}`;
      const qty = budget.quantity ? formatQuantityDisplay(budget.quantity) : '0';

      let body = `BUDGET: ${budget.display_name || 'Untitled'}\n`;
      body += `${'='.repeat(50)}\n\n`;
      body += `Quantity: ${qty}\n`;
      body += `Currency: ${budget.currency_code}\n`;
      body += `Status: ${budget.status || 'DRAFT'}\n\n`;

      BUDGET_SECTION_ORDER.forEach((sectionCode) => {
        const sectionLines = budgetLines.filter(l => l.section === sectionCode && !l.is_margin_row);
        const dataRows = sectionLines.filter(l => !l.is_subtotal);
        if (dataRows.length === 0) return;

        const subtotalLine = sectionLines.find(l => l.is_subtotal);
        body += `${BUDGET_SECTION_LABELS[sectionCode] || sectionCode}\n`;
        body += `${'-'.repeat(40)}\n`;

        dataRows.forEach(l => {
          const amt = l.budget_amount != null ? parseFloat(l.budget_amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-';
          body += `  ${l.line_label || l.description || '-'}: ${budget.currency_code} ${amt}\n`;
        });

        if (subtotalLine) {
          const subAmt = subtotalLine.budget_amount != null ? parseFloat(subtotalLine.budget_amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-';
          body += `  Subtotal: ${budget.currency_code} ${subAmt}\n`;
        }
        body += '\n';
      });

      const grossMarginLine = budgetLines.find(l => l.is_margin_row && l.line_label && l.line_label.toLowerCase().includes('gross'));
      const netMarginLine = budgetLines.find(l => l.is_margin_row && l.line_label && l.line_label.toLowerCase().includes('net'));

      if (grossMarginLine || netMarginLine) {
        body += `MARGIN SUMMARY\n`;
        body += `${'-'.repeat(40)}\n`;
        if (grossMarginLine) {
          const val = parseFloat(grossMarginLine.budget_amount) || 0;
          body += `  Gross Margin: ${budget.currency_code} ${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n`;
        }
        if (netMarginLine) {
          const val = parseFloat(netMarginLine.budget_amount) || 0;
          body += `  Net Margin: ${budget.currency_code} ${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n`;
        }
      }

      const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoUrl;
    } catch (error) {
      console.error('Error generating budget email:', error);
      showError('Failed to generate budget email.');
    }
  };

  // Group deals by group identifier
  const groupedDeals = deals.reduce((acc, deal) => {
    const groupId = deal.offer_group_identifier || `single_${deal.offer_id}`;
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(deal);
    return acc;
  }, {});

  // Add new handlers for trade recap grouping.
  // Keyed on TRANSACTION NUMBER, not the row id: trade recaps and freight
  // recaps live in different tables with independent primary keys, so a bare
  // id means two different rows (TradeRecap #10 vs FreightRecap #10). Ticking
  // one used to select its id-twin as well, and the request the server got was
  // ambiguous — the "grouping freight doesn't work" bug.
  const handleRecapSelection = (transactionNumber) => {
    setSelectedRecaps(prev =>
      prev.includes(transactionNumber)
        ? prev.filter(n => n !== transactionNumber)
        : [...prev, transactionNumber]
    );
  };

  // The actual grouping call, shared by the clean path and the
  // warn-and-proceed path ("Group Anyway" on the mismatch warning modal).
  const performRecapGrouping = async () => {
    try {
      const response = await axiosInstance.post(`/api/offer/trade-recaps/update-groups/?commodity_type=${commodityType}`, {
        recap_identifiers: selectedRecaps
      });

      if (response.data.status === 'success') {
        setRecapGroupingMode(false);
        setSelectedRecaps([]);
        setRecapGroupingError('');
        setShowValidationModal(false);
        setValidationErrors([]);
        fetchData(selectedDate); // Refresh the data
      }
    } catch (error) {
      setRecapGroupingError(error.response?.data?.error || 'Error grouping recaps');
      setShowValidationModal(false);
      setValidationErrors([]);
    }
  };

  const handleGroupRecaps = async () => {
    if (selectedRecaps.length < 2) {
      setRecapGroupingError('Please select at least 2 recaps to group');
      return;
    }

    // Get full recap objects from selected IDs
    const selectedRecapObjects = tradeRecaps.filter(
      r => selectedRecaps.includes(r.transaction_number));

    // Mismatches WARN but never block (Jonas feedback 19.08): show the
    // details and let the user decide to proceed via "Group Anyway".
    const validation = validateRecapGroupCompatibility(selectedRecapObjects);

    if (!validation.valid) {
      setValidationErrors(validation.errors);
      setShowValidationModal(true);
      return;
    }

    await performRecapGrouping();
  };

  const handleUngroupRecaps = async () => {
    if (selectedRecaps.length === 0) {
      setRecapGroupingError('Please select at least one recap to ungroup');
      return;
    }

    try {
      const response = await axiosInstance.post(`/api/offer/trade-recaps/ungroup/?commodity_type=${commodityType}`, {
        recap_identifiers: selectedRecaps
      });

      if (response.data.status === 'success') {
        setRecapGroupingMode(false);
        setSelectedRecaps([]);
        setRecapGroupingError('');
        fetchData(selectedDate); // Refresh the data
      }
    } catch (error) {
      setRecapGroupingError(error.response?.data?.error || 'Error ungrouping recaps');
    }
  };

  // Add groupedRecaps reducer
  const groupedRecaps = tradeRecaps.reduce((acc, recap) => {
    const groupId = recap.trade_recap_grouping_identifier || `single_${recap.id}`;
    if (!acc[groupId]) {
      acc[groupId] = [];
    }
    acc[groupId].push(recap);
    return acc;
  }, {});

  // Entity match verdicts (Aug 2026). Computed OFF groupedRecaps deliberately:
  // a sale/purchase pair created by enquiry conversion carries no grouping
  // identifier, so it is two separate rows above but one deal here. Changing
  // the reducer to fold those together would re-draw the group accent bars and
  // the delivery-assignment target, which is a different feature.
  const recapEntityVerdicts = buildRecapEntityVerdicts(tradeRecaps);

  // Clean up body style on component unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Add viewDelivery function
  const viewDelivery = (deliveryId) => {
    history.push(`/deliveries/${deliveryId}`, {
      selectedDate: selectedDate.toISOString(),
      activeTab: activeTab,
      commodityType: commodityType
    });
  };

  // Relationship handlers
  const handleViewDealRelationships = (deal, e) => {
    if (e) e.stopPropagation();
    setDropdownVisible(null);
    setSelectedDocumentForRelationship(deal);
    setRelationshipDocumentType('deal');
    setShowRelationshipModal(true);
  };

  const handleViewRecapRelationships = (recap, e) => {
    if (e) e.stopPropagation();
    setDropdownVisible(null);
    setSelectedDocumentForRelationship(recap);
    const isFreight = recap.isFreightRecap || recap.transaction_number?.startsWith('F');
    setRelationshipDocumentType(isFreight ? 'freight_recap' : 'recap');
    setShowRelationshipModal(true);
  };

  const handleViewDeliveryRelationships = (delivery, e) => {
    if (e) e.stopPropagation();
    setDropdownVisible(null);
    setSelectedDocumentForRelationship(delivery);
    setRelationshipDocumentType('delivery');
    setShowRelationshipModal(true);
  };

  // Recap actions dropdown
  const renderRecapActions = (recap, groupIndex, recapIndex) => {
    const dropdownKey = `recap-${recap.id}-${groupIndex}-${recapIndex}`;
    return (
    <div className={`action-dropdown ${dropdownVisible === dropdownKey ? 'dropdown-open' : ''}`} ref={el => dropdownRefs.current[dropdownKey] = el}>
      <button className="gear-button" onClick={() => toggleDropdown(dropdownKey)}>
        Actions
      </button>
      {dropdownVisible === dropdownKey && (
        <div className="dropdown-menu">
          <button onClick={() => viewRecap(recap.id)}>
            <FontAwesomeIcon icon={faEye} /> View
          </button>
          <button onClick={(e) => handleViewRecapRelationships(recap, e)}>
            <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
          </button>
          <hr />
          <button className="danger" onClick={() => {
            // Check if recap is associated with a delivery
            const isAssociated = recap.current_status === 'DELIVERY';
            
            setItemToDelete({ 
              id: recap.id, 
              type: 'recap',
              details: {
                transactionNumber: recap.transaction_number,
                transactionType: recap.transaction_type,
                vesselName: recap.vessel_name,
                createdAt: recap.created_at ? formatDateTime(recap.created_at) : 'N/A'
              },
              isWarning: isAssociated,
              warningMessage: isAssociated ? 'Cannot delete this recap because it is associated with a delivery. Please unlink it from the delivery first in the Delivery WHAT screen.' : ''
            });
            setShowPopup(true);
          }}>
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
        </div>
      )}
    </div>
  );
  };

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="trade-operations-layout" data-tour="tradeops-page">
      {/* Shared screen header: brand lockup, module pill tray and the year
          selector. Tab visibility conditions, tints and data-tour hooks are
          unchanged: the bunkers and cargo walkthroughs point at the whole tab
          bar ("tradeops-tabs") and click each pill by its own anchor. */}
      <HMSPageHeader
        title={`Trade Ops ${flavourLabel}`}
        kicker="Operations"
        dataTour="tradeops-header"
        tabs={{
          activeKey: activeTab,
          onChange: setActiveTab,
          dataTour: 'tradeops-tabs',
          items: [
            // Customer traders have no delivery access: Shipment Overview and
            // Deliveries are hidden for them (hideDeliveryTabs, live 2026-09-17).
            ...(!hideDeliveryTabs ? [{
              key: 'shipment-overview',
              label: 'Shipment Overview',
              icon: faGlobeAmericas,
              tint: '#007aff',
              dataTour: 'tradeops-tab-shipment-overview',
            }] : []),
            // An unknown enquiry count (null, e.g. offline) shows no number;
            // a known zero shows "(0)" -- the two must never look alike.
            ...(commodityType === 'fuels' ? [{
              key: 'fuel-enquiries',
              label: `Bunker Enquiries${fuelEnquiriesCount === null ? '' : ` (${fuelEnquiriesCount})`}`,
              icon: faTint,
              tint: '#29b6f6',
              count: fuelEnquiriesCount,
              showZero: true,
              dataTour: 'tradeops-tab-fuel-enquiries',
            }] : []),
            // Bid/Offer is Cargo-only — Bunkers (fuels) goes Enquiry -> Recap
            // directly and the PM dropped the parallel Bid/Offer flow.
            ...(commodityType !== 'fuels' ? [{
              key: 'deals',
              label: `Bid/Offer (${deals.length})`,
              icon: faHandshake,
              tint: '#34c759',
              count: deals.length,
              showZero: true,
              dataTour: 'tradeops-tab-deals',
            }] : []),
            {
              key: 'recaps',
              label: `Trade Recaps (${tradeRecaps.length})`,
              icon: faFileContract,
              tint: '#af52de',
              count: tradeRecaps.length,
              showZero: true,
              dataTour: 'tradeops-tab-recaps',
            },
            ...(!hideDeliveryTabs ? [{
              key: 'deliveries',
              label: `Deliveries (${deliveries.length})`,
              icon: faTruck,
              tint: '#ff9500',
              count: deliveries.length,
              showZero: true,
              dataTour: 'tradeops-tab-deliveries',
            }] : []),
            ...(canSeeBudgetTab ? [{
              key: 'budgets',
              label: 'Budget Calculator',
              icon: faCalculator,
              tint: '#ffcc00',
              dataTour: 'tradeops-tab-budgets',
            }] : []),
            ...(isOperationsHOD ? [{
              key: 'operations-access',
              label: 'Operations Access',
              icon: faUserShield,
              tint: '#8e8e93',
              dataTour: 'tradeops-tab-operations-access',
            }] : []),
            ...(commodityType === 'fuels' && canManageBunkerPorts ? [{
              key: 'bunker-ports',
              label: 'Bunker Ports',
              icon: faAnchor,
              tint: '#2ba355',
              dataTour: 'tradeops-tab-bunker-ports',
            }] : []),
            ...(commodityType !== 'fuels' && canManageCargoPorts ? [{
              key: 'cargo-ports',
              label: 'Cargo Ports',
              icon: faAnchor,
              tint: '#2ba355',
              dataTour: 'tradeops-tab-cargo-ports',
            }] : []),
          ],
        }}
        actions={
          <TimeNavigationBar
            onDateChange={setSelectedDate}
            initialDate={selectedDate}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        }
      />

      {/* Deals Tab Content — Cargo (solids) only. Bunkers no longer has Bid/Offer. */}
      {activeTab === 'deals' && commodityType !== 'fuels' && (
      <div className="tab-content-wrapper" data-tour="tradeops-deals-section">
      <section className="deals-section">
        <div className="button-group">
          <div className="button-group-left">
            <button onClick={() => history.push(`/offer-generator?type=new&dealType=Offer`, navState())} className="action-button" data-tour="tradeops-new-offer">New Offer</button>
            <button onClick={() => history.push(`/offer-generator?type=new&dealType=Bid`, navState())} className="action-button" data-tour="tradeops-new-bid">New Bid</button>
          </div>
          {!groupingMode ? (
            <button onClick={() => setGroupingMode(true)} className="group-button">
              Deal Actions
            </button>
          ) : (
            <div className="grouping-controls">
              <button onClick={handleGroupDeals} className="group-button">Group Selected</button>
              <button onClick={handleUngroupDeals} className="group-button">Ungroup Selected</button>
              <button onClick={() => {
                setGroupingMode(false);
                setSelectedDeals([]);
                setGroupingError('');
              }} className="group-button cancel">
                Cancel
              </button>
            </div>
          )}
        </div>
        {groupingError && <div className="error-message">{groupingError}</div>}
        
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {groupingMode && <th className="checkbox-column">Select</th>}
                <th className="hms-deals-grouping-accent-header"></th>
                <th>Deal Name</th>
                <th>Transaction Number</th>
                <th>Deal Type</th>
                <th>Status</th>
                <th>Deal Validity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedDeals).length > 0 ? (
                Object.entries(groupedDeals).map(([groupId, groupDeals], groupIndex) => (
                  <React.Fragment key={groupId}>
                    {groupDeals.map((deal, dealIndex) => {
                      // Determine if this is a grouped row and its position
                      const isGrouped = groupDeals.length > 1;
                      const isFirstInGroup = dealIndex === 0;
                      const isLastInGroup = dealIndex === groupDeals.length - 1;
                      const isFirstInTable = groupIndex === 0 && dealIndex === 0;
                      const isLastInTable = groupIndex === Object.entries(groupedDeals).length - 1 && isLastInGroup;
                      
                      return (
                        <tr 
                          key={deal.offer_id}
                          onMouseEnter={() => setHoveredDealId(deal.offer_id)}
                          onMouseLeave={() => setHoveredDealId(null)}
                          onClick={(e) => {
                            // Don't navigate if clicking on the action dropdown
                            if (e.target.closest('.action-dropdown')) return;
                            
                            // Don't navigate if in grouping mode or clicking on a checkbox
                            if (groupingMode || e.target.closest('.checkbox-column')) return;
                            
                            viewDeal(
                              deal.offer_id,
                              deal.offer_transaction_type?.toLowerCase(), // Use transaction_type instead of template_type
                              deal.offer_transaction_type,
                              deal.transaction_number
                            );
                          }}
                          className={`
                            ${hoveredDealId === deal.offer_id ? 'highlighted' : ''}
                            ${selectedDeals.includes(deal.offer_id) ? 'selected' : ''}
                            ${isGrouped ? 'grouped-row' : ''}
                            ${isGrouped && isFirstInGroup ? 'grouped-first' : ''}
                            ${isGrouped && isLastInGroup ? 'grouped-last' : ''}
                            ${isFirstInTable ? 'table-first' : ''}
                            ${isLastInTable ? 'table-last' : ''}
                            ${!groupingMode ? 'clickable-row' : ''}
                          `}
                        >
                      
                        {groupingMode && (
                          <td className="checkbox-column" onClick={(e) => {
                            e.stopPropagation();
                            handleDealSelection(deal.offer_id);
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedDeals.includes(deal.offer_id)}
                              onChange={() => {}}
                            />
                          </td>
                        )}
                        <td className="hms-deals-grouping-accent-cell">
                          {groupDeals.length > 1 && isFirstInGroup && (
                            <div 
                              className="hms-deals-grouping-accent-bar"
                              style={{
                                ...generateGroupColor(groupId),
                                height: `calc(${groupDeals.length} * 100% + ${(groupDeals.length - 1) * 1}px - 16px)`
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                console.log('Deal grouping bar clicked - not implemented yet');
                              }}
                            >
                              <span className="hms-deals-grouping-accent-bar-text">
                                Group {groupIndex + 1}
                              </span>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="deal-name-badge">
                            <FontAwesomeIcon icon={faHandshake} className="name-icon" />
                            <span className="name-text">
                              {deal.display_name || `${deal.offer_transaction_type} Deal`}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className={`transaction-number-badge ${deal.offer_transaction_type === 'Offer' ? 'offer-transaction' : 'bid-transaction'}`}>
                            <FontAwesomeIcon icon={faHashtag} className="transaction-icon" />
                            <span className="transaction-text">{deal.transaction_number || 'N/A'}</span>
                          </div>
                        </td>
                        <td>
                          <div className={`recap-type-badge ${deal.offer_transaction_type.toLowerCase()}`}>
                            <FontAwesomeIcon icon={deal.offer_transaction_type === 'Offer' ? faArrowUp : faArrowDown} className="recap-type-icon" />
                            <span className="recap-type-text">{deal.offer_transaction_type.toUpperCase()}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge status-${deal.offer_current_status.toLowerCase().replace(/\s+/g, '-')}`}>
                            {deal.offer_current_status}
                          </span>
                        </td>
                        <td>
                          {deal.offer_is_indication_only ? (
                            <div className="deal-validity-badge indication-only">
                              <FontAwesomeIcon icon={faInfoCircle} className="validity-icon" />
                              <span className="validity-text">Indication Only</span>
                            </div>
                          ) : deal.deal_validity_date && deal.deal_validity_time ? (
                            <div className={`deal-validity-badge ${isValidityExpired(deal.deal_validity_date, deal.deal_validity_time) ? 'expired' : ''}`}>
                              <FontAwesomeIcon icon={faClock} className="validity-icon" />
                              <span className="validity-text">
                                {formatValidityDisplayDateTime(deal.deal_validity_date, deal.deal_validity_time)}
                              </span>
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>
                          <div className={`action-dropdown ${dropdownVisible === deal.offer_id ? 'dropdown-open' : ''}`} ref={el => dropdownRefs.current[deal.offer_id] = el}>
                            <button className="gear-button" onClick={() => toggleDropdown(deal.offer_id)}>
                              Actions
                            </button>
                            {dropdownVisible === deal.offer_id && (
                              <div className="dropdown-menu">
                                <button onClick={() => viewDeal(
                                  deal.offer_id,
                                  deal.offer_transaction_type?.toLowerCase(), // Use transaction_type instead of template_type
                                  deal.offer_transaction_type,
                                  deal.transaction_number
                                )}>
                                  <FontAwesomeIcon icon={faEye} /> View
                                </button>
                                <button onClick={() => pinToDesk('deal', deal.offer_id)}>
                                  <FontAwesomeIcon icon={faThumbtack} /> Pin to My Workspace
                                </button>
                                <button onClick={(e) => handleViewDealRelationships(deal, e)}>
                                  <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                                </button>
                                <button onClick={() => handleGenerateClick(deal)}>
                                  <FontAwesomeIcon icon={faFileAlt} /> Generate
                                </button>
                                {/* Manual Dormant toggle (PM request). Label
                                    and icon flip based on current status so
                                    the same slot acts as park/reactivate. */}
                                {deal.offer_current_status === 'Dormant' ? (
                                  <button onClick={() => handleToggleDormant(deal)}>
                                    <FontAwesomeIcon icon={faPlay} /> Reactivate
                                  </button>
                                ) : (
                                  <button onClick={() => handleToggleDormant(deal)}>
                                    <FontAwesomeIcon icon={faMoon} /> Mark as Dormant
                                  </button>
                                )}
                                <hr />
                                <button className="danger" onClick={() => {
                                  setItemToDelete({ 
                                    id: deal.offer_id, 
                                    type: 'deal',
                                    details: {
                                      transactionNumber: deal.transaction_number,
                                      transactionType: deal.offer_transaction_type,
                                      createdAt: deal.creation_timestamp ? formatDateTime(deal.creation_timestamp) : 'N/A'
                                    }
                                  });
                                  setShowPopup(true);
                                }}>
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={groupingMode ? 8 : 7} className="empty-state-cell">
                    <div className="empty-state-container">
                      <div className="empty-state-icon">
                        <FontAwesomeIcon icon={faHandshake} />
                      </div>
                      <div className="empty-state-text">
                        <h3>No Deals Yet</h3>
                        <p>Deals for this year will appear here</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      </div>
      )}

      {/* Trade Recaps Tab Content */}
      {activeTab === 'recaps' && (
      <div className="tab-content-wrapper" data-tour="tradeops-recaps-section">
      <section className="trade-recaps-section">
        <div className="button-group">
          <div className="button-group-left" data-tour="tradeops-recap-actions">
            <button onClick={handleNewRecapSale} className="action-button" data-tour="tradeops-new-sales-recap">Sales Recap</button>
            <button onClick={handleNewRecapPurchase} className="action-button" data-tour="tradeops-new-purchase-recap">Purchase Recap</button>
            <button onClick={handleNewRecapFreight} className="action-button" data-tour="tradeops-new-freight-recap">Freight Recap</button>
          </div>
          {!canGroupRecaps ? null : !recapGroupingMode ? (
            <button onClick={() => setRecapGroupingMode(true)} className="group-button">
              Recap Actions
            </button>
          ) : (
            <div className="grouping-controls">
              <button onClick={handleGroupRecaps} className="group-button">Group Selected</button>
              <button onClick={handleUngroupRecaps} className="group-button">Ungroup Selected</button>
              <button onClick={() => {
                setRecapGroupingMode(false);
                setSelectedRecaps([]);
                setRecapGroupingError('');
              }} className="group-button cancel">
                Cancel
              </button>
            </div>
          )}
        </div>
        {recapGroupingError && <div className="error-message">{recapGroupingError}</div>}
        
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {recapGroupingMode && <th className="checkbox-column">Select</th>}
                <th className="hms-deals-grouping-accent-header"></th>
                <th>Recap Name</th>
                <th>Transaction Number</th>
                <th>Recap Type</th>
                <th>Entity</th>
                {commodityType !== 'fuels' && <th>Associated Deal</th>}
                <th>Status</th>
                <th>Total Qty</th>
                <th>Allocated</th>
                <th>Available</th>
                <th>Allocation Status</th>
                <th>Creation Timestamp</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedRecaps).length > 0 ? (
                Object.entries(groupedRecaps).map(([groupId, groupRecaps], groupIndex) => (
                  <React.Fragment key={groupId}>
                    {groupRecaps.map((recap, recapIndex) => {
                      const recapType = recap.recap_type || 
                        (recap.purchase_or_sale === 'sale' ? 'Sale' : 
                         recap.purchase_or_sale === 'purchase' ? 'Purchase' : 
                         recap.transaction_number?.startsWith('F') ? 'Freight' : 'Purchase');
                      
                      // Find associated deal transaction number
                      const associatedDeal = deals.find(deal => deal.offer_id === recap.associated_offer_or_bid);
                      const associatedDealNumber = associatedDeal?.transaction_number;
                      
                      // Determine if this is a grouped row and its position
                      const isGrouped = groupRecaps.length > 1;
                      const isFirstInGroup = recapIndex === 0;
                      const isLastInGroup = recapIndex === groupRecaps.length - 1;
                      const isFirstInTable = groupIndex === 0 && recapIndex === 0;
                      const isLastInTable = groupIndex === Object.entries(groupedRecaps).length - 1 && isLastInGroup;
                      
                      return (
                        <tr 
                          key={recap.id}
                          onClick={(e) => {
                            // Don't navigate if clicking on the action dropdown or checkbox
                            if (e.target.closest('.action-dropdown') || e.target.closest('.checkbox-column')) return;
                            
                            // Don't navigate if in recap grouping mode
                            if (recapGroupingMode) return;
                            
                            viewRecap(recap.id);
                          }}
                          className={`
                            ${selectedRecaps.includes(recap.transaction_number) ? 'selected' : ''}
                            ${isGrouped ? 'grouped-row' : ''}
                            ${isGrouped && isFirstInGroup ? 'grouped-first' : ''}
                            ${isGrouped && isLastInGroup ? 'grouped-last' : ''}
                            ${isFirstInTable ? 'table-first' : ''}
                            ${isLastInTable ? 'table-last' : ''}
                            ${!recapGroupingMode ? 'clickable-row' : ''}
                          `}
                        >
                          {recapGroupingMode && (
                            <td className="checkbox-column" onClick={(e) => {
                              e.stopPropagation();
                              handleRecapSelection(recap.transaction_number);
                            }}>
                              <input
                                type="checkbox"
                                checked={selectedRecaps.includes(recap.transaction_number)}
                                onChange={() => {}}
                              />
                            </td>
                          )}
                          <td className="hms-deals-grouping-accent-cell">
                            {groupRecaps.length > 1 && isFirstInGroup && (() => {
                              // Check if ANY recap in this group has a delivery assigned
                              // This ensures the delivery number stays visible even if some recaps are unlinked
                              const recapWithDelivery = groupRecaps.find(r => 
                                r.associated_delivery_number && 
                                r.associated_delivery_id && 
                                r.current_status === 'DELIVERY'
                              );
                              
                              const deliveryNumber = recapWithDelivery?.associated_delivery_number;
                              const deliveryId = recapWithDelivery?.associated_delivery_id;
                              const hasDelivery = !!recapWithDelivery;
                              
                              return (
                                <div 
                                  className="hms-deals-grouping-accent-bar"
                                  style={{
                                    ...generateGroupColor(groupId),
                                    height: `calc(${groupRecaps.length} * 100% + ${(groupRecaps.length - 1) * 1}px - 16px)`
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    
                                    if (hasDelivery) {
                                      // Navigate to existing delivery using ID
                                      history.push(`/deliveries/${deliveryId}`, {
                                        selectedDate: selectedDate.toISOString(),
                                        activeTab: activeTab,
                                        commodityType: commodityType
                                      });
                                    } else {
                                      setCurrentGroupRecaps(groupRecaps);
                                      setShowAssignDeliveryModal(true);
                                    }
                                  }}
                                >
                                  <span className="hms-deals-grouping-accent-bar-text">
                                    {hasDelivery ? deliveryNumber : 'ASSIGN DELIVERY'}
                                  </span>
                                </div>
                              );
                            })()}
                          </td>
                          <td>
                            <div className={`recap-name-badge ${recapType.toLowerCase()}`}>
                              <FontAwesomeIcon icon={faFileContract} className="name-icon" />
                              <span className="name-text">
                                {/* A trade recap's name is the server's (offer/deal_names.py),
                                    blank when every part is; a freight recap keeps its own
                                    (utils/recapDisplayName.js). */}
                                {isFreightRow(recap)
                                  ? buildRecapDisplayName(recap, { profile: RECAP_NAME_PROFILES.FREIGHT })
                                  : (recap.display_name
                                    || ((recapType === 'Sale' || recap.recap_type === 'SALE') ? 'Sale Recap' : 'Purchase Recap'))}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className={`transaction-number-badge ${
                              recap.transaction_number?.startsWith('S') ? 'sale-transaction' : 
                              recap.transaction_number?.startsWith('P') ? 'purchase-transaction' : 
                              recap.transaction_number?.startsWith('F') ? 'freight-transaction' : 'purchase-transaction'
                            }`}>
                              <FontAwesomeIcon icon={
                                recap.transaction_number?.startsWith('S') ? faArrowCircleUp :
                                recap.transaction_number?.startsWith('P') ? faArrowCircleDown :
                                recap.transaction_number?.startsWith('F') ? faShip : faArrowCircleDown
                              } className="transaction-icon" />
                              <span className="transaction-text">
                                {recap.transaction_number || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className={`recap-type-badge ${recap.recap_type?.toLowerCase() || recap.purchase_or_sale}`}>
                              <span className="recap-type-text">{recapType}</span>
                            </div>
                          </td>
                          <td>
                            {/* Green = this deal's sale and purchase legs sit on the
                                same HMS legal entity, red = they don't. Neutral when
                                the entity is unset or the leg has no counterpart --
                                "not filled in" must not read as "mismatch". */}
                            {(() => {
                              const verdict = recapEntityVerdict(recapEntityVerdicts, recap);
                              const entityName = recap.hms_entity_display;
                              return (
                                <div
                                  className={`recap-entity-badge entity-${verdict}`}
                                  title={
                                    verdict === 'mismatch'
                                      ? 'Sale and purchase legs are on DIFFERENT IMT entities'
                                      : verdict === 'match'
                                        ? 'Sale and purchase legs are on the same IMT entity'
                                        : undefined
                                  }
                                >
                                  <span className="recap-entity-text">{entityName || '—'}</span>
                                </div>
                              );
                            })()}
                          </td>
                          {commodityType !== 'fuels' && (
                          <td>
                            {associatedDealNumber ? (
                              <div className={`transaction-number-badge ${associatedDeal.offer_transaction_type === 'Offer' ? 'offer-transaction' : 'bid-transaction'}`}>
                                <FontAwesomeIcon icon={faFileInvoice} className="transaction-icon" />
                                <span className="transaction-text">
                                  {associatedDealNumber}
                                </span>
                              </div>
                            ) : (
                              <div className="transaction-number-badge none-badge">
                                <span className="transaction-text">None</span>
                              </div>
                            )}
                          </td>
                          )}
                          <td>
                            <span className={`status-badge status-${recap.current_status?.toLowerCase() || 'active'}`}>
                              {recap.current_status || 'ACTIVE'}
                            </span>
                          </td>
                          
                          {/* PARTIAL ALLOCATION COLUMNS (NEW) - SINGLE BADGE FOR ALL THREE */}
                          <td colspan="3">
                            <div className="allocation-group-badge">
                              <div className="allocation-group-item">
                                <span className="allocation-group-value">
                                  {(() => {
                                    const isSale = recapType === 'Sale' || recap.recap_type === 'SALE';
                                    const totalQty = isSale ? recap.sale_contract_quantity : recap.purchase_contract_quantity;
                                    const unit = isSale ? (recap.sale_contract_quantity_unit_name || recap.sale_contract_quantity_unit) : (recap.purchase_contract_quantity_unit_name || recap.purchase_contract_quantity_unit);
                                    return totalQty ? `${totalQty} ${unit || 'MT'}` : 'N/A';
                                  })()}
                                </span>
                              </div>
                              <div className="allocation-group-divider"></div>
                              <div className="allocation-group-item">
                                <span className="allocation-group-value">
                                  {recap.total_allocated !== undefined && recap.total_allocated !== null
                                    ? `${recap.total_allocated} ${(() => {
                                        const isSale = recapType === 'Sale' || recap.recap_type === 'SALE';
                                        return isSale ? (recap.sale_contract_quantity_unit_name || recap.sale_contract_quantity_unit) : (recap.purchase_contract_quantity_unit_name || recap.purchase_contract_quantity_unit);
                                      })() || 'MT'}`
                                    : '0'}
                                </span>
                              </div>
                              <div className="allocation-group-divider"></div>
                              <div className="allocation-group-item">
                                <span className="allocation-group-value">
                                  {recap.available_quantity !== undefined && recap.available_quantity !== null
                                    ? `${recap.available_quantity} ${(() => {
                                        const isSale = recapType === 'Sale' || recap.recap_type === 'SALE';
                                        return isSale ? (recap.sale_contract_quantity_unit_name || recap.sale_contract_quantity_unit) : (recap.purchase_contract_quantity_unit_name || recap.purchase_contract_quantity_unit);
                                      })() || 'MT'}`
                                    : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`allocation-status-badge ${
                              recap.allocation_status === 'Available' ? 'status-available' :
                              recap.allocation_status === 'Partially Allocated' ? 'status-partial' :
                              recap.allocation_status === 'Fully Allocated' ? 'status-full' :
                              'status-available'
                            }`}>
                              {recap.allocation_status || 'Available'}
                            </span>
                          </td>
                          
                          <td>
                            <div className="created-at-badge">
                              <FontAwesomeIcon icon={faClock} className="created-at-icon" />
                              <span className="created-at-text">
                                {formatDateTime(recap.created_at)}
                              </span>
                            </div>
                          </td>
                          <td>
                            {renderRecapActions(recap, groupIndex, recapIndex)}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={(recapGroupingMode ? 14 : 13) - (commodityType === 'fuels' ? 1 : 0)} className="empty-state-cell">
                    <div className="empty-state-container">
                      <div className="empty-state-icon">
                        <FontAwesomeIcon icon={faFileContract} />
                      </div>
                      <div className="empty-state-text">
                        <h3>No Trade Recaps Yet</h3>
                        <p>Trade recaps for this year will appear here</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      </div>
      )}

      {/* Deliveries Tab Content */}
      {activeTab === 'deliveries' && (
      <div className="tab-content-wrapper" data-tour="tradeops-deliveries-section">
      <section className="deliveries-section">
        <div className="button-group">
          <div className="button-group-left">
            <button onClick={handleNewDelivery} className="action-button" data-tour="tradeops-new-delivery">Create Delivery</button>
          </div>
        </div>
        
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Delivery Name</th>
                <th>Delivery Number</th>
                <th>Vessel Name</th>
                <th>Associated Recaps</th>
                <th>Status</th>
                <th>Creation Timestamp</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.length > 0 ? (
                deliveries.map(delivery => (
                  <tr 
                    key={delivery.id}
                    onClick={(e) => {
                      // Don't navigate if clicking on the action dropdown
                      if (e.target.closest('.action-dropdown')) return;
                      
                      viewDelivery(delivery.id);
                    }}
                    className="clickable-row"
                  >
                    <td>
                      {delivery.display_name ? (
                        <div className="delivery-name-badge">
                          <FontAwesomeIcon icon={faTruck} className="name-icon" />
                          <span className="name-text">{delivery.display_name}</span>
                        </div>
                      ) : (
                        <div className="delivery-name-badge">
                          <FontAwesomeIcon icon={faTruck} className="name-icon" />
                          <span className="name-text">{`Delivery ${delivery.delivery_unique_number || ''}`}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="transaction-number-badge">
                        <FontAwesomeIcon icon={faBoxes} className="transaction-icon" />
                        <span className="transaction-text">{delivery.delivery_unique_number || 'N/A'}</span>
                      </div>
                    </td>
                    <td>
                      {delivery.vessel_name ? (
                        <div className="vessel-badge">
                          <FontAwesomeIcon icon={faShip} className="vessel-icon" />
                          <span className="vessel-text">{delivery.vessel_name}</span>
                        </div>
                      ) : (
                        <div className="transaction-number-badge none-badge">
                          <span className="transaction-text">None</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="trade-recap-hover-container">
                        {delivery.associated_trade_recap_numbers?.trade_recap_numbers?.length ? (
                          <>
                            <div className="trade-recap-summary-badge">
                              <FontAwesomeIcon icon={faFileInvoice} className="trade-recap-summary-icon" />
                              <span className="trade-recap-summary-text">
                                {delivery.associated_trade_recap_numbers.trade_recap_numbers.length} Recap{delivery.associated_trade_recap_numbers.trade_recap_numbers.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="trade-recap-details-dropdown">
                              {delivery.associated_trade_recap_numbers.trade_recap_numbers.map((recapNumber, index) => (
                                <div key={index} className="trade-recap-detail-badge">
                                  <FontAwesomeIcon icon={faFileInvoice} className="trade-recap-detail-icon" />
                                  <span className="trade-recap-detail-text">{recapNumber}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="trade-recap-summary-badge">
                            <FontAwesomeIcon icon={faFileInvoice} className="trade-recap-summary-icon" />
                            <span className="trade-recap-summary-text">None</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge status-${(delivery.status || 'DRAFT').toLowerCase()}`}>
                        {delivery.status === 'ACTIVE' ? 'Workflowed' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <div className="created-at-badge">
                        <FontAwesomeIcon icon={faClock} className="created-at-icon" />
                        <span className="created-at-text">
                          {formatDateTime(delivery.creation_timestamp)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className={`action-dropdown ${dropdownVisible === `delivery-${delivery.id}` ? 'dropdown-open' : ''}`} ref={el => dropdownRefs.current[`delivery-${delivery.id}`] = el}>
                        <button className="gear-button" onClick={() => toggleDropdown(`delivery-${delivery.id}`)}>
                          Actions
                        </button>
                        {dropdownVisible === `delivery-${delivery.id}` && (
                          <div className="dropdown-menu">
                            <button onClick={() => viewDelivery(delivery.id)}>
                              <FontAwesomeIcon icon={faEye} /> View
                            </button>
                            <button onClick={(e) => handleViewDeliveryRelationships(delivery, e)}>
                              <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                            </button>
                            <hr />
                            <button className="danger" onClick={() => {
                              setItemToDelete({ 
                                id: delivery.id, 
                                type: 'delivery',
                                details: {
                                  deliveryNumber: delivery.delivery_unique_number,
                                  vesselName: delivery.vessel_name,
                                  createdAt: delivery.creation_timestamp ? formatDateTime(delivery.creation_timestamp) : 'N/A'
                                }
                              });
                              setShowPopup(true);
                            }}>
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="empty-state-cell">
                    <div className="empty-state-container">
                      <div className="empty-state-icon">
                        <FontAwesomeIcon icon={faTruck} />
                      </div>
                      <div className="empty-state-text">
                        <h3>No Deliveries Yet</h3>
                        <p>Deliveries for this year will appear here</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      </div>
      )}

      {/* Shipment Overview Tab Content */}
      {activeTab === 'shipment-overview' && (
        <div className="tab-content-wrapper">
          <ShipmentOverviewTab deliveries={deliveries} onOpenDelivery={viewDelivery} />
        </div>
      )}

      {/* Operations Access Tab Content (HOD only) */}
      {activeTab === 'operations-access' && isOperationsHOD && (
        <div className="tab-content-wrapper">
          <OperationsAccessList commodityType={commodityType} />
        </div>
      )}

      {/* Fuel Enquiries Tab Content (Fuels flavour only — Sprint 1D) */}
      {activeTab === 'fuel-enquiries' && commodityType === 'fuels' && (
        <FuelEnquiriesTab commodityType={commodityType} />
      )}

      {/* Bunker Ports Tab Content (Fuels flavour + bunker-ports permission only) */}
      {activeTab === 'bunker-ports' && commodityType === 'fuels' && canManageBunkerPorts && (
        <div className="tab-content-wrapper">
          <BunkerPortsManager />
        </div>
      )}

      {/* Cargo Ports Tab Content (Solids flavour + cargo-ports permission only) */}
      {activeTab === 'cargo-ports' && commodityType !== 'fuels' && canManageCargoPorts && (
        <div className="tab-content-wrapper">
          <CargoPortsManager />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showPopup && itemToDelete && (
        <TradeOpsDeleteModal
          isOpen={showPopup}
          onClose={() => {
            setShowPopup(false);
            setItemToDelete(null);
          }}
          onConfirm={() => {
            if (itemToDelete.type === 'deal') {
              deleteDeal(itemToDelete.id);
            } else if (itemToDelete.type === 'recap') {
              deleteRecap(itemToDelete.id);
            } else if (itemToDelete.type === 'delivery') {
              deleteDelivery(itemToDelete.id);
            } else if (itemToDelete.type === 'budget') {
              deleteBudget(itemToDelete.id);
            }
          }}
          itemType={itemToDelete.type}
          itemDetails={itemToDelete.details}
          isWarning={itemToDelete.isWarning || false}
          warningMessage={itemToDelete.warningMessage || ''}
          isDeleting={isDeleting}
        />
      )}

      {/* PDF Popup */}
      <PdfPreviewModal
        isOpen={showPdfPopup}
        pdfUrl={pdfUrl}
        onClose={() => setShowPdfPopup(false)}
        onDownload={handleDownloadPdf}
      />

      {/* Format Selection Popup */}
      {showFormatPopup && (
        <div className="format-popup-overlay" onClick={() => setShowFormatPopup(false)}>
          <div className="format-popup-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="format-popup-header">
              <div className="format-popup-header-content">
                <h3 className="format-popup-title">
                  Generate Deal Document
                </h3>
              </div>
              <button className="format-popup-close-btn" onClick={() => setShowFormatPopup(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="format-popup-body">
              <div className="format-popup-description">
                <FontAwesomeIcon icon={faFileContract} className="format-popup-description-icon" />
                <p>Choose the format for your deal document. All formats contain the complete deal information.</p>
              </div>

              {/* Format Options Cards */}
              <div className="format-options-container">
                <div className="format-option-card" onClick={() => handleFormatSelect('pdf')}>
                  <div className="format-option-icon pdf-icon">
                    <FontAwesomeIcon icon={faFilePdf} />
                  </div>
                  <div className="format-option-content">
                    <h4>PDF Document</h4>
                    <p>Standard, shareable format</p>
                  </div>
                  <div className="format-option-arrow">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>

                <div className="format-option-card" onClick={() => handleFormatSelect('docx')}>
                  <div className="format-option-icon word-icon">
                    <FontAwesomeIcon icon={faFileWord} />
                  </div>
                  <div className="format-option-content">
                    <h4>Word Document</h4>
                    <p>Editable format for customization</p>
                  </div>
                  <div className="format-option-arrow">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>

                <div className="format-option-card" onClick={() => handleFormatSelect('email')}>
                  <div className="format-option-icon email-icon">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <div className="format-option-content">
                    <h4>Email Draft</h4>
                    <p>Opens in your default email client</p>
                  </div>
                  <div className="format-option-arrow">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="format-popup-footer">
              <button className="format-popup-btn-secondary" onClick={() => setShowFormatPopup(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Format Selection Popup */}
      {showBudgetFormatPopup && (
        <div className="format-popup-overlay" onClick={() => setShowBudgetFormatPopup(false)}>
          <div className="format-popup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="format-popup-header">
              <div className="format-popup-header-content">
                <h3 className="format-popup-title">
                  Generate Budget Document
                </h3>
              </div>
              <button className="format-popup-close-btn" onClick={() => setShowBudgetFormatPopup(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="format-popup-body">
              <div className="format-popup-description">
                <FontAwesomeIcon icon={faCalculator} className="format-popup-description-icon" />
                <p>Choose the format for your budget document. All formats contain the complete budget information.</p>
              </div>

              <div className="format-options-container">
                <div className="format-option-card" onClick={() => handleBudgetFormatSelect('pdf')}>
                  <div className="format-option-icon pdf-icon">
                    <FontAwesomeIcon icon={faFilePdf} />
                  </div>
                  <div className="format-option-content">
                    <h4>PDF Document</h4>
                    <p>Standard, shareable format</p>
                  </div>
                  <div className="format-option-arrow">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>

                <div className="format-option-card" onClick={() => handleBudgetFormatSelect('docx')}>
                  <div className="format-option-icon word-icon">
                    <FontAwesomeIcon icon={faFileWord} />
                  </div>
                  <div className="format-option-content">
                    <h4>Word Document</h4>
                    <p>Editable format for customization</p>
                  </div>
                  <div className="format-option-arrow">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>

                <div className="format-option-card" onClick={() => handleBudgetFormatSelect('email')}>
                  <div className="format-option-icon email-icon">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </div>
                  <div className="format-option-content">
                    <h4>Email Draft</h4>
                    <p>Opens in your default email client</p>
                  </div>
                  <div className="format-option-arrow">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </div>
                </div>
              </div>
            </div>

            <div className="format-popup-footer">
              <button className="format-popup-btn-secondary" onClick={() => setShowBudgetFormatPopup(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budgets Tab Content */}
      {activeTab === 'budgets' && canSeeBudgetTab && (
        <div className="tab-content-wrapper">
          <section className="deals-section">
            <div className="button-group">
              <div className="button-group-left">
                <button onClick={() => history.push('/budget-calculator/new', navState())} className="action-button">
                  New Budget
                </button>
              </div>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Quantity</th>
                    <th>Currency</th>
                    <th>Gross Margin</th>
                    <th>Net Margin</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetsLoading ? (
                    [...Array(4)].map((_, i) => (
                      /* One bar per column so the placeholder keeps the real
                         table's geometry. `height` restates the 14px the local
                         block used -- the primitive's `text` shape is 0.8em,
                         which off this 11px table would come out at 8.8px. */
                      <tr key={i} className="budget-skeleton-row">
                        <td><HMSSkeleton width="60%" height={14} /></td>
                        <td><HMSSkeleton width="50%" height={14} /></td>
                        <td><HMSSkeleton width="40%" height={14} /></td>
                        <td><HMSSkeleton width="55%" height={14} /></td>
                        <td><HMSSkeleton width="55%" height={14} /></td>
                        <td><HMSSkeleton width="45%" height={14} /></td>
                        <td><HMSSkeleton width="60px" height={14} /></td>
                      </tr>
                    ))
                  ) : budgets.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state-cell">
                        <div className="empty-state-container">
                          <div className="empty-state-icon">
                            <FontAwesomeIcon icon={faCalculator} />
                          </div>
                          <div className="empty-state-text">
                            <h3>No Budgets Yet</h3>
                            <p>Click "New Budget" to create one</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    budgets.map((b) => {
                      const bLines = b.lines || [];
                      const grossLine = bLines.find(l => l.is_margin_row && l.line_label && l.line_label.toLowerCase().includes('gross'));
                      const netLine = bLines.find(l => l.is_margin_row && l.line_label && l.line_label.toLowerCase().includes('net'));
                      const grossVal = grossLine ? parseFloat(grossLine.budget_amount) || 0 : null;
                      const netVal = netLine ? parseFloat(netLine.budget_amount) || 0 : null;

                      const renderMarginBadge = (val, currency) => {
                        if (val === null) return <span style={{ color: '#999' }}>-</span>;
                        const sign = val > 0 ? 'profit' : val < 0 ? 'loss' : 'zero';
                        return (
                          <span className={`budget-margin-label margin-${sign}`}>
                            {currency} {val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        );
                      };

                      return (
                      <tr
                        key={b.id}
                        className="clickable-row"
                        onClick={(e) => {
                          if (e.target.closest('.action-dropdown')) return;
                          history.push(`/budget-calculator/${b.id}`, navState());
                        }}
                      >
                        <td>{b.display_name || 'Untitled'}</td>
                        <td>{b.quantity}</td>
                        <td>{b.currency_code}</td>
                        <td>{renderMarginBadge(grossVal, b.currency_code)}</td>
                        <td>{renderMarginBadge(netVal, b.currency_code)}</td>
                        <td>{b.created_at ? new Date(b.created_at).toLocaleDateString() : '-'}</td>
                        <td>
                          <div
                            className={`action-dropdown ${dropdownVisible === `budget-${b.id}` ? 'dropdown-open' : ''}`}
                            ref={el => dropdownRefs.current[`budget-${b.id}`] = el}
                          >
                            <button className="gear-button" onClick={(e) => { e.stopPropagation(); toggleDropdown(`budget-${b.id}`); }}>
                              Actions
                            </button>
                            {dropdownVisible === `budget-${b.id}` && (
                              <div className="dropdown-menu">
                                <button onClick={(e) => { e.stopPropagation(); closeDropdown(); history.push(`/budget-calculator/${b.id}`, navState()); }}>
                                  Open
                                </button>
                                <button onClick={async (e) => {
                                  e.stopPropagation();
                                  closeDropdown();
                                  try {
                                    const fullBudget = await budgetService.getHeader(b.id);
                                    setBudgetToGenerate(fullBudget);
                                    setShowBudgetFormatPopup(true);
                                  } catch (err) {
                                    console.error('Error loading budget for export:', err);
                                    showError('Failed to load budget data for export.');
                                  }
                                }}>
                                  <FontAwesomeIcon icon={faFileAlt} /> Generate
                                </button>
                                <hr />
                                <button className="danger" onClick={(e) => {
                                  e.stopPropagation();
                                  closeDropdown();
                                  setItemToDelete({
                                    id: b.id,
                                    type: 'budget',
                                    details: {
                                      budgetName: b.display_name || 'Untitled',
                                      currency: b.currency_code,
                                      quantity: b.quantity,
                                      createdAt: b.created_at ? new Date(b.created_at).toLocaleDateString() : 'N/A',
                                    }
                                  });
                                  setShowPopup(true);
                                }}>
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {/* Hamster Chat Integration */}
      <HamsterFloatingButton />

      {/* HMS Footer */}
      <Footer />
      
      {/* Assign Delivery Modal */}
      <AssignDeliveryModal
        isOpen={showAssignDeliveryModal}
        onClose={() => {
          setShowAssignDeliveryModal(false);
          setCurrentGroupRecaps([]);
        }}
        groupedRecapIds={currentGroupRecaps.map(r => r.id)}
        groupedRecaps={currentGroupRecaps}
        onSuccess={(deliveryId, recaps) => {
          if (deliveryId === 'new') {
            // Navigate to new delivery with pre-linked recaps
            history.push('/deliveries/new', {
              preLinkedRecaps: {
                purchase: recaps.filter(r => r.recap_type === 'PURCHASE'),
                sales: recaps.filter(r => r.recap_type === 'SALE'),
                freight: recaps.filter(r => r.recap_type === 'Freight' || r.isFreightRecap)
              },
              commodityType: commodityType,
              returnTo: {
                path: '/trade-operations',
                state: {
                  selectedDate: selectedDate.toISOString(),
                  activeTab: activeTab,
                  commodityType: commodityType
                }
              }
            });
          } else {
            // Navigate to existing delivery
            history.push(`/deliveries/${deliveryId}`, {
              selectedDate: selectedDate.toISOString(),
              activeTab: activeTab,
              commodityType: commodityType
            });
          }
        }}
      />

      {/* Relationship Modal */}
      <InvoiceRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => setShowRelationshipModal(false)}
        document={selectedDocumentForRelationship}
        documentType={relationshipDocumentType}
      />
      
      {/* Recap Grouping Validation Modal — warns on mismatch, never blocks */}
      <RecapGroupingValidationModal
        isOpen={showValidationModal}
        onClose={() => {
          setShowValidationModal(false);
          setValidationErrors([]);
        }}
        onConfirm={performRecapGrouping}
        errors={validationErrors}
      />
      </div>
    </Layout>
  );
};

export default TradeOperationsLayout; 